<script>
  import { createEventDispatcher, onMount, tick } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";
  import {
    BadgeColor,
    Tooltip,
    TextInput,
    Button,
    InfoBlock,
  } from "paxray-components-svelte";
  import Modal from "lib/components/Modal.svelte";
  import PaxbotTableActionButton from "./PaxbotTableActionButton.svelte";
  import PaxbotInlineEditableCell from "./PaxbotInlineEditableCell.svelte";
  import PaxAiDepartmentPopover from "./PaxAiDepartmentPopover.svelte";
  import PaxbotFullscreenChat from "./PaxbotFullscreenChat.svelte";
  import TeamSurveySetup from "./TeamSurveySetup.svelte";
  import {
    activityContextFields,
    reviewFitQuestions,
    unknownAnswer,
    assessActivity,
    activityOpenItems,
    mergeActivityReviews,
  } from "lib/util/frameworkReadiness.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { ARROW_LEFT_ICON, HOME_ICON, PLUS_ICON } from "@utils/icons.js";

  export let started = false;
  export let moveToBottomOnSubmit = false;
  export let submitted = false;
  export let processingPreview = false;
  export let progressStep = 0;
  export let busy = false;

  let step = 1;
  let generationMode = "bottom_up";
  const hierarchyPlaceholders = {
    l1: "e.g. Procure-to-Pay",
    l2: "e.g. Invoice handling",
    l3: "e.g. Check invoice",
    l4: "e.g. Compare invoice amount",
  };
  const frameworkProcessSuggestions = {
    Finance: [
      "Record-to-Report",
      "Procure-to-Pay",
      "Order-to-Cash",
      "Budget-to-Plan",
      "Treasury-to-Cash",
    ],
    Sales: [
      "Lead-to-Order",
      "Quote-to-Cash",
      "Account-to-Retention",
      "Opportunity-to-Contract",
      "Forecast-to-Plan",
    ],
    Operations: [
      "Plan-to-Produce",
      "Request-to-Delivery",
      "Schedule-to-Execute",
      "Asset-to-Maintain",
      "Issue-to-Resolution",
    ],
    "Human Resources": [
      "Hire-to-Retire",
      "Request-to-Hire",
      "Onboard-to-Perform",
      "Learn-to-Grow",
      "Time-to-Pay",
    ],
    HR: [
      "Hire-to-Retire",
      "Request-to-Hire",
      "Onboard-to-Perform",
      "Learn-to-Grow",
      "Time-to-Pay",
    ],
    IT: [
      "Request-to-Resolution",
      "Incident-to-Resolution",
      "Change-to-Release",
      "Plan-to-Deploy",
      "Asset-to-Maintain",
    ],
    Marketing: [
      "Idea-to-Market",
      "Market-to-Lead",
      "Campaign-to-Conversion",
      "Content-to-Engagement",
      "Launch-to-Measure",
    ],
    Procurement: [
      "Procure-to-Pay",
      "Source-to-Contract",
      "Request-to-Order",
      "Supplier-to-Performance",
      "Invoice-to-Pay",
    ],
    "Customer Service": [
      "Service-to-Resolution",
      "Case-to-Closure",
      "Complaint-to-Resolution",
      "Contact-to-Case",
      "Service-to-Retention",
    ],
    Production: [
      "Plan-to-Produce",
      "Order-to-Deliver",
      "Schedule-to-Execute",
      "Quality-to-Release",
      "Asset-to-Maintain",
    ],
    "Supply Chain": [
      "Plan-to-Fulfill",
      "Source-to-Deliver",
      "Order-to-Deliver",
      "Demand-to-Plan",
      "Inventory-to-Availability",
    ],
    Global: [
      "Lead-to-Order",
      "Order-to-Cash",
      "Procure-to-Pay",
      "Plan-to-Produce",
      "Hire-to-Retire",
      "Record-to-Report",
      "Idea-to-Market",
      "Service-to-Resolution",
    ],
    _default: [
      "Lead-to-Order",
      "Order-to-Cash",
      "Procure-to-Pay",
      "Plan-to-Produce",
      "Hire-to-Retire",
      "Record-to-Report",
      "Idea-to-Market",
      "Service-to-Resolution",
    ],
  };
  let loadedDepartments = [];
  let loadingDepartments = true;
  let departmentLoadError = "";
  // Gebunden von der Seite FrameworkAssistant.svelte fuer den Streifen rechts (wachsendes Framework)
  export let selectedAreas = [];
  // Aus einem Projekt geoeffnet (Configure Framework): dann gibt es den Weg "spaeter - erst das Team"
  export let projectId = "";
  export let projectDepartments = []; // gefuehrt aus einem Projekt: dessen Abteilungen zuerst
  export let projectTeams = [];
  export let selectedTeams = [];
  let savingTeams = false;
  let teamSaveError = "";
  export let busyTeamIds = []; // Teams mit Umfrage - je Team gibt es nur eine // [{ id, name } vorhandenes | { name } neues Team] - fuer die Team-Umfragen
  const startPaths = [
    {
      id: "framework",
      title: "Upload a framework",
      text: "Best if you already have a process structure, e.g. as Excel.",
    },
    {
      id: "documents",
      title: "Upload documents",
      text: "Turn process diagrams or work instructions into a framework",
    },
    {
      id: "describe",
      title: "Describe the work",
      text: "Best if you know the work well yourself. Describe the daily tasks and Pax AI suggests a framework.",
    },
    {
      id: "later",
      title: "Ask your team",
      text: "Best if the knowledge sits with your team. Each team lists its daily tasks in a short survey.",
    },
  ];
  let startPath = "later";
  let workEntryStarted = false;

  // Team-Umfrage ohne Projekt: laufende Projekte zur Auswahl, passende zur gewaehlten Abteilung zuerst bzw. allein
  let surveyProjects = [];
  let surveyProjectsLoaded = false;
  $: if (startPath === "later" && !projectId && !surveyProjectsLoaded)
    loadSurveyProjects();

  async function loadSurveyProjects() {
    surveyProjectsLoaded = true;
    try {
      const response = await fetchWithSession("/api/damprojects/projects");
      const running = response.ok ? (await response.json()).running || [] : [];
      const department = (selectedAreas[0] || "").toLowerCase();
      const matching = running.filter((project) =>
        (project.departments || []).some(
          (item) => (item?.name || "").toLowerCase() === department,
        ),
      );
      surveyProjects = (matching.length ? matching : running).map(
        ({ id, name }) => ({ id, name }),
      );
    } catch {
      // ohne Liste bleibt der Hinweis auf Configure Framework
    }
  }

  // Das Projekt steht im Anker und wird beim Laden gelesen (FrameworkAssistant.svelte) - deshalb neu laden; der Entwurf
  // bleibt im Browser und fuehrt zurueck zu diesem Schritt
  function openInProject(id) {
    window.location.hash = `project=${id}`;
    window.location.reload();
  }
  let selectedL1Processes = [];
  export let frameworkDraft = [];
  let taskMiningAssessments = [];
  let existingDepartmentFramework = [];
  let activeInlineCell = "";
  let undoHistory = [];
  let reviewSearch = "";
  let reviewFilter = "all";
  let collapsedProcesses = [];
  let reviewTable;
  let lastMissingCell = "";
  let pendingAnalysis = null;
  let analysisPreviewOpen = false;
  let rowEditorOpen = false;
  let rowEditorMode = "add";
  let rowEditorSource = null;
  let rowEditorDraft = { l1: "", l2: "", l3: "", l4: "" };
  let mergeTargetKey = "";
  let rowEditorError = "";
  let departmentBeforeEdit = null;
  let editingDepartment = false;
  const hierarchyLevels = ["l1", "l2", "l3", "l4"];
  const hierarchyLabels = {
    l1: "Process (L1)",
    l2: "Subprocess (L2)",
    l3: "Task (L3)",
    l4: "Subtask (L4)",
  };
  let deleteModalOpen = false;
  let deleteRow = null;
  let inlineChangeModalOpen = false;
  let pendingInlineChange = null;
  let fitReviewModalOpen = false;
  let fitReviewRow = null;
  let fitReviewAnswers = {};
  let fitReviewContext = {};
  let draftSaveMessage = "";
  let taskMiningAssessmentMode = false;
  let currentL1Index = 0;
  let currentL2Index = 0;
  let currentL3Index = 0;
  let hybridL2Knowledge = "";
  let message = "";
  let workDescriptionDraft = "";
  let workContext = {};
  let pendingDepartments = [];
  let awaitingDepartmentConfirmation = false;
  // Gefuehrt aus einem Projekt (Rueckmeldung 04.10.: zu viel auf einmal): je Schritt eine Frage und die Auswahl,
  // Eingabefelder erst auf Wunsch
  let newDepartmentOpen = false;
  let newDepartmentName = "";
  let departmentSelectionError = "";
  let confirmingDepartments = false;
  let departmentConfirmationError = "";
  let analyzingWork = false;
  let creatingFramework = false;
  export let frameworkCreated = false;
  let workAnalysisError = "";
  let restoringDraft = true;
  // Transfer aus der Team-Umfrage (Configure Framework): diese Aufgabenliste wird ohne Eingabe analysiert,
  // sobald die Abteilung feststeht
  let autoAnalyzeText = "";
  const draftStorageKey = "paxray.framework-assistant.draft";
  const dispatch = createEventDispatcher();
  const navigate = useNavigate();

  onMount(async () => {
    if (!processingPreview) restoreDraft();
    if (processingPreview) {
      started = true;
      step = 6;
      selectedAreas = ["Human Resources"];
      submitted = true;
      frameworkDraft = [
        {
          name: "Hire-to-Retire",
          l2: [
            {
              name: "Recruitment",
              l3: [
                {
                  name: "Manage applications",
                  l4: [
                    "Review applications",
                    "Screen candidates",
                    "Schedule interviews",
                    "Conduct interviews",
                    "Collect interview feedback",
                    "Prepare job offer",
                    "Approve job offer",
                    "Send job offer",
                    "Prepare employment contract",
                    "Notify candidates",
                  ],
                },
              ],
            },
          ],
        },
      ];
      taskMiningAssessments = frameworkDraft[0].l2[0].l3[0].l4.map((l4) => ({
        l1: "Hire-to-Retire",
        l2: "Recruitment",
        l3: "Manage applications",
        l4,
        status: "needs_clarification",
        reason:
          "We need a little more context to assess this subtask for Task Mining.",
        questions: [
          {
            id: "digital",
            question: "Is this subtask mainly performed in digital systems?",
            options: [
              "Yes",
              "Partly, with handoffs or waiting",
              "Mainly offline",
            ],
          },
          {
            id: "continuity",
            question:
              "Can one person complete this subtask in one continuous work session?",
            options: ["Yes", "No"],
          },
          {
            id: "frequency",
            question: "How often is this subtask performed?",
            options: [
              "Daily or weekly",
              "Monthly or quarterly",
              "Yearly or less often",
            ],
          },
        ],
      }));
      message = "";
    }
    restoringDraft = false;

    await loadDepartments();
  });

  async function loadDepartments() {
    loadingDepartments = true;
    departmentLoadError = "";
    try {
      const response = await fetchWithSession("/api/damprojects/departments");
      if (!response.ok) throw new Error("Could not load departments");

      const departments = await response.json();
      const organizationDepartments = Array.from(
        new Set(
          (departments || [])
            .map((department) =>
              typeof department === "string" ? department : department?.name,
            )
            .map((name) => name?.trim())
            .filter(Boolean),
        ),
      );
      loadedDepartments = organizationDepartments;
    } catch (error) {
      departmentLoadError =
        "Departments could not be loaded. Please retry before continuing.";
      console.error("Could not load organization departments:", error);
    } finally {
      loadingDepartments = false;
    }
  }

  $: surveyTeams = [
    ...projectTeams,
    ...selectedTeams.filter((team) => !team.id),
  ].filter((team) => !busyTeamIds.includes(team.id));
  $: availableDepartments = Array.from(
    new Set([...projectDepartments, ...loadedDepartments]),
  );
  $: currentOptions =
    step === 1
      ? projectDepartments.length
        ? projectDepartments
        : loadedDepartments.length > 4
          ? []
          : loadedDepartments
      : step === 2
        ? getProcessSuggestions(selectedAreas)
        : [];
  $: currentL1 = frameworkDraft[currentL1Index];
  $: l2Entries = frameworkDraft.flatMap((l1) =>
    l1.l2.map((l2) => ({ l1, l2 })),
  );
  $: currentL2Entry = l2Entries[currentL2Index];
  $: l3Entries = l2Entries.flatMap(({ l1, l2 }) =>
    l2.l3.map((l3) => ({ l1, l2, l3 })),
  );
  $: currentL3Entry = l3Entries[currentL3Index];
  $: frameworkRows = frameworkDraft.flatMap((l1, l1Index) =>
    (l1.l2 || []).flatMap((l2, l2Index) =>
      (l2.l3 || []).flatMap((l3, l3Index) => {
        const activities = l3.l4 || [];
        return (activities.length ? activities : [""]).map((l4, l4Index) => {
          const assessment = findTaskMiningAssessment(
            l1.name,
            l2.name,
            l3.name,
            l4,
          );
          return {
            l1Index,
            l2Index,
            l3Index,
            l4Index,
            rowKey: `${l1Index}-${l2Index}-${l3Index}-${l4Index}`,
            department: selectedAreas[0] || "",
            l1: l1.name,
            l2: l2.name,
            l3: l3.name,
            l4,
            l1New: !hasExistingL1(l1.name),
            l2New: !hasExistingL2(l1.name, l2.name),
            l3New: !hasExistingL3(l1.name, l2.name, l3.name),
            l1Missing: !l1.name,
            l2Missing: !l2.name,
            l3Missing: !l3.name,
            l4Missing: !l4,
            l4New: Boolean(l4) && !hasExistingL4(l1.name, l2.name, l3.name, l4),
            fitStatus: assessment?.status || "needs_clarification",
            fitReason:
              assessment?.reason || "This subtask has not been assessed yet.",
            fitQuestions: reviewFitQuestions.map((question) => ({
              ...question,
              options: [...question.options, unknownAnswer],
              labels: { ...question.labels, [unknownAnswer]: "I don't know" },
            })),
            context: assessment?.context || {},
            included:
              assessment?.included ?? assessment?.status !== "not_suitable",
          };
        });
      }),
    ),
  );
  $: reviewableTaskMiningRows = frameworkRows.filter(
    (row) => row.included && activityOpenItems(row).length > 0,
  );
  $: frameworkComplete =
    frameworkRows.length > 0 &&
    frameworkRows.every((row) =>
      hierarchyLevels.every(
        (level) => Boolean(row[level]?.trim()) && row[level] !== "—",
      ),
    );
  $: pilotRows = frameworkRows.filter((row) => row.included);
  $: pilotOpenRows = pilotRows.filter((row) => activityOpenItems(row).length);
  $: pilotReady =
    Boolean(selectedAreas[0]) &&
    pilotRows.length > 0 &&
    pilotOpenRows.length === 0;
  $: missingCells = frameworkRows.flatMap((row) =>
    hierarchyLevels
      .filter((level) => !row[level]?.trim())
      .map((level) => ({ row, level, key: `${row.rowKey}-${level}` })),
  );
  $: filteredRows = frameworkRows.filter(
    (row) =>
      (!reviewSearch.trim() ||
        hierarchyLevels.some((level) =>
          normalizeName(row[level]).includes(normalizeName(reviewSearch)),
        )) &&
      (reviewFilter === "all" ||
        (reviewFilter === "incomplete" &&
          hierarchyLevels.some((level) => !row[level]?.trim())) ||
        (reviewFilter === "clarification" &&
          row.included &&
          activityOpenItems(row).length > 0)),
  );
  $: processGroups = Array.from(new Set(filteredRows.map((row) => row.l1))).map(
    (name) => ({ name, rows: filteredRows.filter((row) => row.l1 === name) }),
  );
  $: busy = analyzingWork || creatingFramework || confirmingDepartments;
  $: if (autoAnalyzeText && step === 7 && !restoringDraft && !busy)
    startAutoAnalysis();

  async function startAutoAnalysis() {
    const text = autoAnalyzeText;
    autoAnalyzeText = "";
    await analyzeWork(text, []);
    if (workAnalysisError) message = text; // zum erneuten Senden sichtbar machen
  }
  $: progressStep = frameworkCreated ? 3 : step === 6 ? 2 : step === 7 ? 1 : 0;
  $: analysisChanges = pendingAnalysis
    ? compareFrameworks(frameworkDraft, pendingAnalysis.framework)
    : { added: [], removed: [] };
  $: rowEditorValid =
    rowEditorMode === "merge"
      ? Boolean(mergeTargetKey)
      : hierarchyLevels.every((level) => rowEditorDraft[level].trim());
  $: fitReviewResolution = assessActivity(fitReviewAnswers);
  $: fitReviewKnockout = fitReviewResolution.status === "not_suitable";
  $: fitReviewNotice = {
    not_suitable: { type: "warning", heading: "Not suitable for Task Mining" },
    needs_clarification: { type: "info", heading: "Needs clarification" },
    suitable: { type: "hint", heading: "Suitable for Task Mining" },
  }[fitReviewResolution.status];
  // Offline or rare work is excluded entirely, so splitting it would not help
  $: fitReviewNeedsSplit =
    fitReviewAnswers.digital !== "Mainly offline" &&
    fitReviewAnswers.frequency !== "Yearly or less often" &&
    (fitReviewAnswers.continuity === "No" ||
      fitReviewAnswers.continuity === "Partly, with handoffs or waiting");
  $: fitReviewQuestionsComplete =
    Boolean(fitReviewRow) &&
    fitReviewRow.fitQuestions.every((question) =>
      Boolean(fitReviewAnswers[question.id]),
    );
  $: if (!restoringDraft && started && step === 0) {
    generationMode = "bottom_up";
    step = 1;
  }
  $: currentGreeting = awaitingDepartmentConfirmation
    ? "New departments found"
    : frameworkCreated
      ? "Addition added to framework"
      : step === 0
        ? "Build a framework addition bottom-up"
        : step === 1
          ? projectId
            ? "Which department are you setting up?"
            : "Which department is this addition for?"
          : step === 8
            ? `Which teams work in ${selectedAreas[0] || "this department"}?`
            : step === 7
              ? workEntryStarted
                ? startPath === "later"
                  ? "Set up a team survey"
                  : startPath === "framework"
                    ? "Upload your framework"
                    : startPath === "documents"
                      ? "Upload your documents"
                      : `Describe the work in ${selectedAreas.join(", ") || "this area"}`
                : projectId
                  ? `How would you like to build the framework for ${selectedAreas.join(", ") || "this area"}?`
                  : `Which activities make up the work in ${selectedAreas.join(", ") || "this area"}?`
              : step === 2
                ? "What would you like to add to this department?"
                : step === 3
                  ? generationMode === "hybrid"
                    ? `Which subprocesses do you already know for ${currentL1?.name || "this area"}?`
                    : `Which activities happen every day as part of ${currentL1?.name || "this process"}?`
                  : step === 4
                    ? generationMode === "hybrid"
                      ? `What do you do in ${currentL2Entry?.l2.name || "this subprocess"}?`
                      : `What happens within ${currentL2Entry?.l2.name || "this process"}?`
                    : step === 5
                      ? "Which individual subtasks are involved?"
                      : "Review your framework";

  function normalizeName(name) {
    return String(name || "")
      .trim()
      .toLocaleLowerCase();
  }

  function findTaskMiningAssessment(l1, l2, l3, l4) {
    return taskMiningAssessments.find(
      (assessment) =>
        normalizeName(assessment.l1) === normalizeName(l1) &&
        normalizeName(assessment.l2) === normalizeName(l2) &&
        normalizeName(assessment.l3) === normalizeName(l3) &&
        normalizeName(assessment.l4) === normalizeName(l4),
    );
  }

  function taskMiningFitLabel(status) {
    if (status === "suitable") return "Suitable";
    if (status === "not_suitable") return "Not suitable";
    return "Needs clarification";
  }

  function taskMiningFitColor(status) {
    if (status === "suitable") return "green";
    if (status === "not_suitable") return "red";
    return "red";
  }

  function taskMiningFitTooltip(row) {
    const questions = row.fitQuestions.map(
      (question) => `• ${question.question}`,
    );
    return [
      taskMiningFitLabel(row.fitStatus),
      row.fitReason,
      questions.length ? `Open questions:\n${questions.join("\n")}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  }

  function sameTaskMiningPath(assessment, row) {
    return (
      normalizeName(assessment.l1) === normalizeName(row.l1) &&
      normalizeName(assessment.l2) === normalizeName(row.l2) &&
      normalizeName(assessment.l3) === normalizeName(row.l3) &&
      normalizeName(assessment.l4) === normalizeName(row.l4)
    );
  }

  function openTaskMiningReview(row = reviewableTaskMiningRows[0]) {
    if (!row || !row.fitQuestions.length) return;
    fitReviewRow = row;
    fitReviewAnswers = {
      ...(findTaskMiningAssessment(row.l1, row.l2, row.l3, row.l4)?.answers ||
        {}),
    };
    // "No" from the survey means the same as handoffs or waiting in the modal
    if (fitReviewAnswers.continuity === "No") {
      fitReviewAnswers.continuity = "Partly, with handoffs or waiting";
    }
    fitReviewContext = { ...row.context };
    fitReviewModalOpen = true;
  }

  function closeTaskMiningReview() {
    fitReviewModalOpen = false;
    fitReviewRow = null;
    fitReviewAnswers = {};
  }

  function answerTaskMiningQuestion(questionId, option) {
    fitReviewAnswers = { ...fitReviewAnswers, [questionId]: option };
  }

  function resolvedTaskMiningAssessment() {
    return assessActivity(fitReviewAnswers);
  }

  function togglePilotActivity(row) {
    rememberDraft();
    const existing = findTaskMiningAssessment(row.l1, row.l2, row.l3, row.l4);
    taskMiningAssessments = taskMiningAssessments.filter(
      (entry) => !sameTaskMiningPath(entry, row),
    );
    taskMiningAssessments = [
      ...taskMiningAssessments,
      {
        ...existing,
        l1: row.l1,
        l2: row.l2,
        l3: row.l3,
        l4: row.l4,
        included: !row.included,
      },
    ];
    frameworkDraft = frameworkDraft;
  }

  async function suggestActivitySplit() {
    if (!fitReviewRow || busy) return;
    const row = fitReviewRow;
    const context = activityContextFields
      .map(
        (field) =>
          `${field.label} ${fitReviewContext[field.key] || unknownAnswer}`,
      )
      .join("\n");
    saveTaskMiningReview();
    await analyzeWork(
      `Split the subtask ${[row.l1, row.l2, row.l3, row.l4].join(" → ")} into 2 to 4 smaller, clearly bounded subtasks under the same task. Split at changes of person, handoffs, or waiting for others. Where details are unknown, base the split on the typical steps of this kind of work as implied by the names. Keep all other subtasks unchanged and return the complete updated framework in the required JSON format. Context:\n${context}`,
      [],
    );
    // Without a real change the preview would only show an empty list
    if (pendingAnalysis) {
      const changes = compareFrameworks(frameworkDraft, pendingAnalysis.framework);
      if (!changes.added.length && !changes.removed.length) {
        pendingAnalysis = null;
        analysisPreviewOpen = false;
        workAnalysisError =
          "No smaller subtasks could be suggested. Describe the individual steps in the chat below, for example which steps happen before and after the handoff.";
      }
    }
  }

  function closeTaskMiningAssessment() {
    reviewFilter = "all";
    taskMiningAssessmentMode = false;
  }

  // "Save for later" keeps the answers but postpones excluding the subtask
  function saveTaskMiningReviewForLater() {
    saveTaskMiningReview({ later: true });
  }

  // Called as a click handler too, so the option is read from an object
  function saveTaskMiningReview({ later = false } = {}) {
    if (!fitReviewRow || busy) return;

    rememberDraft();
    const currentRow = fitReviewRow;
    const assessed = resolvedTaskMiningAssessment();
    const resolution =
      later && assessed.status === "not_suitable"
        ? { ...assessed, status: "needs_clarification" }
        : assessed;
    // Only an automatic exclusion is lifted. A manual exclusion stays
    const wasExcludedAsNotSuitable = currentRow.fitStatus === "not_suitable";
    const nextAssessment = {
      l1: currentRow.l1,
      l2: currentRow.l2,
      l3: currentRow.l3,
      l4: currentRow.l4,
      ...resolution,
      questions: [],
      answers: fitReviewAnswers,
      assessmentSource: "user_reviewed",
      context: { ...fitReviewContext },
      included:
        resolution.status !== "not_suitable" &&
        (wasExcludedAsNotSuitable || (currentRow.included ?? true)),
    };
    const hasExistingAssessment = taskMiningAssessments.some((assessment) =>
      sameTaskMiningPath(assessment, currentRow),
    );
    taskMiningAssessments = hasExistingAssessment
      ? taskMiningAssessments.map((assessment) =>
          sameTaskMiningPath(assessment, currentRow)
            ? nextAssessment
            : assessment,
        )
      : [...taskMiningAssessments, nextAssessment];
    // Rebuild the flattened rows because their fit data is path-matched from
    // taskMiningAssessments inside a helper function.
    frameworkDraft = frameworkDraft;

    closeTaskMiningReview();
  }

  function existingL1Entries(name) {
    return existingDepartmentFramework.filter(
      (entry) => normalizeName(entry.l1) === normalizeName(name),
    );
  }

  function availableL1Entries(name) {
    return [...existingDepartmentFramework, ...frameworkDraft].filter(
      (entry) => normalizeName(entry.name || entry.l1) === normalizeName(name),
    );
  }

  function hasExistingL1(name) {
    return existingL1Entries(name).length > 0;
  }

  function hasExistingL2(l1Name, l2Name) {
    return existingL1Entries(l1Name).some((entry) =>
      (entry.l2 || []).some(
        (l2) => normalizeName(l2.name) === normalizeName(l2Name),
      ),
    );
  }

  function hasExistingL3(l1Name, l2Name, l3Name) {
    return existingL1Entries(l1Name).some((entry) =>
      (entry.l2 || []).some(
        (l2) =>
          normalizeName(l2.name) === normalizeName(l2Name) &&
          (l2.l3 || []).some(
            (l3) => normalizeName(l3.name) === normalizeName(l3Name),
          ),
      ),
    );
  }

  function hasExistingL4(l1Name, l2Name, l3Name, l4Name) {
    return existingL1Entries(l1Name).some((entry) =>
      (entry.l2 || []).some(
        (l2) =>
          normalizeName(l2.name) === normalizeName(l2Name) &&
          (l2.l3 || []).some(
            (l3) =>
              normalizeName(l3.name) === normalizeName(l3Name) &&
              (l3.l4 || []).some(
                (l4) => normalizeName(l4) === normalizeName(l4Name),
              ),
          ),
      ),
    );
  }

  function uniqueNames(values) {
    return Array.from(new Set(values.filter(Boolean))).sort((left, right) =>
      left.localeCompare(right),
    );
  }

  function getInlineOptions(row, level) {
    const availableFramework = [
      ...existingDepartmentFramework,
      ...frameworkDraft,
    ];
    if (level === "l1") {
      return uniqueNames(
        availableFramework.map((entry) => entry.name || entry.l1),
      );
    }
    const l1Entries = availableL1Entries(row.l1);
    const l2Entries = l1Entries
      .flatMap((entry) => entry.l2 || [])
      .filter(
        (entry) =>
          level === "l2" || normalizeName(entry.name) === normalizeName(row.l2),
      );
    if (level === "l2")
      return uniqueNames(
        l1Entries.flatMap((entry) => (entry.l2 || []).map((item) => item.name)),
      );
    if (level === "l3")
      return uniqueNames(
        l2Entries.flatMap((entry) => (entry.l3 || []).map((item) => item.name)),
      );
    return uniqueNames(
      l2Entries
        .flatMap((entry) => entry.l3 || [])
        .filter((entry) => normalizeName(entry.name) === normalizeName(row.l3))
        .flatMap((entry) => entry.l4 || []),
    );
  }

  function draftSnapshot() {
    return JSON.parse(
      JSON.stringify({
        frameworkDraft,
        taskMiningAssessments,
        selectedAreas,
        existingDepartmentFramework,
      }),
    );
  }

  function rememberDraft(snapshot = draftSnapshot()) {
    undoHistory = [...undoHistory.slice(-29), snapshot];
  }

  function undoChange() {
    const previous = undoHistory[undoHistory.length - 1];
    if (!previous) return;
    ({
      frameworkDraft,
      taskMiningAssessments,
      selectedAreas,
      existingDepartmentFramework,
    } = previous);
    undoHistory = undoHistory.slice(0, -1);
    activeInlineCell = "";
    collapsedProcesses = [];
  }

  function sameBranch(candidate, row, level) {
    return hierarchyLevels
      .slice(0, hierarchyLevels.indexOf(level) + 1)
      .every((key) => candidate[key] === row[key]);
  }

  function updateInlineRow(row, level, value, applyAll = false) {
    if (!value || value === "—" || value === row[level]) return;
    const targetRows = applyAll
      ? frameworkRows.filter((candidate) => sameBranch(candidate, row, level))
      : [row];
    const targetKeys = new Set(targetRows.map((target) => target.rowKey));
    const parentLevels = hierarchyLevels.slice(
      0,
      hierarchyLevels.indexOf(level),
    );
    const existingSibling = frameworkRows.some(
      (candidate) =>
        !targetKeys.has(candidate.rowKey) &&
        normalizeName(candidate[level]) === normalizeName(value) &&
        parentLevels.every(
          (key) => normalizeName(candidate[key]) === normalizeName(row[key]),
        ),
    );
    if (existingSibling) {
      workAnalysisError =
        "This name already exists in this branch. Use Move or Merge to combine entries explicitly.";
      return;
    }
    const nextRows = frameworkRows.map((candidate) =>
      targetKeys.has(candidate.rowKey)
        ? { ...candidate, [level]: value }
        : candidate,
    );
    const createsDuplicate = nextRows.some(
      (candidate, index) =>
        targetKeys.has(candidate.rowKey) &&
        nextRows.some(
          (other, otherIndex) =>
            index !== otherIndex && sameTaskMiningPath(candidate, other),
        ),
    );
    if (createsDuplicate) {
      workAnalysisError =
        "This name would create a duplicate subtask. Use Move or Merge to combine entries explicitly.";
      return;
    }
    rememberDraft();
    workAnalysisError = "";
    // Keep assessments attached when only a label changes, including a single row of a shared branch.
    taskMiningAssessments = nextRows.flatMap((nextRow, index) => {
      const oldRow = frameworkRows[index];
      const assessment = taskMiningAssessments.find((entry) =>
        sameTaskMiningPath(entry, oldRow),
      );
      return assessment
        ? [
            {
              ...assessment,
              l1: nextRow.l1,
              l2: nextRow.l2,
              l3: nextRow.l3,
              l4: nextRow.l4,
            },
          ]
        : [];
    });
    frameworkDraft = buildFrameworkFromRows(nextRows);
  }

  function buildFrameworkFromRows(rows) {
    const framework = [];
    rows.forEach((row) => {
      let l1 = framework.find((entry) => entry.name === row.l1);
      if (!l1) {
        l1 = { name: row.l1, l2: [] };
        framework.push(l1);
      }
      let l2 = l1.l2.find((entry) => entry.name === row.l2);
      if (!l2) {
        l2 = { name: row.l2, l3: [] };
        l1.l2.push(l2);
      }
      let l3 = l2.l3.find((entry) => entry.name === row.l3);
      if (!l3) {
        l3 = { name: row.l3, l4: [] };
        l2.l3.push(l3);
      }
      if (row.l4) l3.l4.push(row.l4);
    });
    return framework;
  }

  function handleInlineChange(row, level, event) {
    const value = String(event.detail.value || "").trim();
    activeInlineCell = "";
    const matchingRows = frameworkRows.filter((candidate) =>
      sameBranch(candidate, row, level),
    );
    if (matchingRows.length > 1) {
      pendingInlineChange = { row, level, value, count: matchingRows.length };
      inlineChangeModalOpen = true;
      return;
    }
    updateInlineRow(row, level, value);
  }

  function applyPendingInlineChange(applyAll) {
    if (!pendingInlineChange) return;
    updateInlineRow(
      pendingInlineChange.row,
      pendingInlineChange.level,
      pendingInlineChange.value,
      applyAll,
    );
    pendingInlineChange = null;
    inlineChangeModalOpen = false;
  }

  function askRemovePreviewRow(row) {
    activeInlineCell = "";
    deleteRow = row;
    deleteModalOpen = true;
  }

  function removePreviewRow() {
    if (!deleteRow) return;
    rememberDraft();
    const nextFramework = JSON.parse(JSON.stringify(frameworkDraft));
    const l1 = nextFramework[deleteRow.l1Index];
    const l2 = l1?.l2?.[deleteRow.l2Index];
    const l3 = l2?.l3?.[deleteRow.l3Index];
    if (!l1 || !l2 || !l3) return;
    l3.l4.splice(deleteRow.l4Index, 1);
    if (!l3.l4.length) l2.l3.splice(deleteRow.l3Index, 1);
    if (!l2.l3.length) l1.l2.splice(deleteRow.l2Index, 1);
    if (!l1.l2.length) nextFramework.splice(deleteRow.l1Index, 1);
    frameworkDraft = nextFramework;
    deleteModalOpen = false;
    deleteRow = null;
  }

  function flattenDraft(framework) {
    return framework.flatMap((l1) =>
      (l1.l2 || []).flatMap((l2) =>
        (l2.l3 || []).flatMap((l3) =>
          (l3.l4?.length ? l3.l4 : [""]).map((l4) => ({
            l1: l1.name,
            l2: l2.name,
            l3: l3.name,
            l4,
          })),
        ),
      ),
    );
  }

  function compareFrameworks(before, after) {
    const remaining = [...flattenDraft(after)];
    const removed = [];
    for (const row of flattenDraft(before)) {
      const index = remaining.findIndex((candidate) =>
        hierarchyLevels.every((level) => candidate[level] === row[level]),
      );
      if (index < 0) removed.push(row);
      else remaining.splice(index, 1);
    }
    return { added: remaining, removed };
  }

  function acceptAnalysis() {
    if (!pendingAnalysis) return;
    rememberDraft();
    frameworkDraft = pendingAnalysis.framework;
    taskMiningAssessments = mergeActivityReviews(
      frameworkDraft,
      pendingAnalysis.taskMiningAssessment || [],
      taskMiningAssessments,
    );
    existingDepartmentFramework = pendingAnalysis.departmentFramework || [];
    pendingAnalysis = null;
    analysisPreviewOpen = false;
    collapsedProcesses = [];
    message = "";
  }

  function discardAnalysis() {
    pendingAnalysis = null;
    analysisPreviewOpen = false;
  }

  function toggleProcess(name) {
    collapsedProcesses = collapsedProcesses.includes(name)
      ? collapsedProcesses.filter((entry) => entry !== name)
      : [...collapsedProcesses, name];
  }

  async function focusNextMissingCell() {
    if (!missingCells.length) return;
    const index = missingCells.findIndex(
      (cell) => cell.key === lastMissingCell,
    );
    const cell = missingCells[(index + 1) % missingCells.length];
    lastMissingCell = cell.key;
    reviewSearch = "";
    reviewFilter = "all";
    collapsedProcesses = collapsedProcesses.filter(
      (name) => name !== cell.row.l1,
    );
    await tick();
    const button = reviewTable?.querySelector(
      `[data-row-key="${cell.row.rowKey}"] [data-level="${cell.level}"] button`,
    );
    button?.scrollIntoView({ block: "center", behavior: "smooth" });
    button?.click();
  }

  function openRowEditor(mode, row = null) {
    rowEditorMode = mode;
    rowEditorSource = row;
    rowEditorDraft = Object.fromEntries(
      hierarchyLevels.map((level) => [level, row?.[level] || ""]),
    );
    if (mode === "duplicate") rowEditorDraft.l4 = `${row.l4} (copy)`;
    mergeTargetKey = "";
    rowEditorError = "";
    activeInlineCell = "";
    rowEditorOpen = true;
  }

  function applyRowEditor() {
    if (!rowEditorValid) return;
    const source = rowEditorSource;
    let nextRows;
    if (rowEditorMode === "merge") {
      const target = frameworkRows.find((row) => row.rowKey === mergeTargetKey);
      if (!target || !source || target.rowKey === source.rowKey) return;
      nextRows = frameworkRows.filter((row) => row.rowKey !== source.rowKey);
    } else {
      const nextRow = Object.fromEntries(
        hierarchyLevels.map((level) => [level, rowEditorDraft[level].trim()]),
      );
      const otherRows = frameworkRows.filter(
        (row) => rowEditorMode !== "move" || row.rowKey !== source?.rowKey,
      );
      if (otherRows.some((row) => sameTaskMiningPath(row, nextRow))) {
        rowEditorError =
          "This subtask already exists at this location. Choose a different name or use Merge.";
        return;
      }
      nextRows =
        rowEditorMode === "move"
          ? frameworkRows.map((row) =>
              row.rowKey === source.rowKey ? nextRow : row,
            )
          : [...frameworkRows, nextRow];
    }
    rememberDraft();
    frameworkDraft = buildFrameworkFromRows(nextRows);
    // A moved or merged subtask needs a fresh fit assessment for its new context.
    if (source && (rowEditorMode === "move" || rowEditorMode === "merge")) {
      const target = nextRows.find((row) => row.rowKey === mergeTargetKey);
      taskMiningAssessments = taskMiningAssessments.filter(
        (assessment) =>
          !sameTaskMiningPath(assessment, source) &&
          (!target || !sameTaskMiningPath(assessment, target)),
      );
      const changedRow = rowEditorMode === "move" ? rowEditorDraft : target;
      if (changedRow)
        taskMiningAssessments = [
          ...taskMiningAssessments,
          {
            ...Object.fromEntries(
              hierarchyLevels.map((level) => [level, changedRow[level].trim()]),
            ),
            included:
              rowEditorMode === "move" ? source.included : target.included,
            status: "needs_clarification",
            reason: "Review the subtask in its changed context.",
          },
        ];
      frameworkDraft = frameworkDraft;
    }
    reviewSearch = "";
    reviewFilter = "all";
    collapsedProcesses = [];
    rowEditorOpen = false;
  }

  function changeDepartment() {
    departmentBeforeEdit = draftSnapshot();
    editingDepartment = true;
    step = 1;
    message = selectedAreas[0] || "";
    submitted = false;
  }

  function finishDepartmentSelection() {
    if (editingDepartment) {
      if (selectedAreas[0] !== departmentBeforeEdit.selectedAreas[0]) {
        rememberDraft(departmentBeforeEdit);
        existingDepartmentFramework = [];
        taskMiningAssessments = taskMiningAssessments.map((assessment) => ({
          l1: assessment.l1,
          l2: assessment.l2,
          l3: assessment.l3,
          l4: assessment.l4,
          included: assessment.included,
          status: "needs_clarification",
          reason: "Confirm the subtask details for the new department.",
        }));
        frameworkDraft = frameworkDraft;
      }
      editingDepartment = false;
      departmentBeforeEdit = null;
      step = 6;
      submitted = true;
    } else {
      // Gefuehrt aus einem Projekt: eine Frage pro Schritt - nach der Abteilung die Teams (Schritt 8)
      // Vorerst deaktiviert: direkt zur Wahl des Startwegs (Schritt 7), Team-Schritt bleibt fuer spaeter erhalten
      // step = generationMode === "bottom_up" ? (projectId ? 8 : 7) : 2;
      step = generationMode === "bottom_up" ? 7 : 2;
      workEntryStarted = false;
      submitted = false;
    }
    message = step === 7 ? workDescriptionDraft : "";
  }

  const teamKey = (team) => team.id || `new:${normalizeName(team.name)}`;
  const isTeamSelected = (team) =>
    selectedTeams.some((item) => teamKey(item) === teamKey(team));

  function toggleTeam(team) {
    if (savingTeams) return;
    selectedTeams = isTeamSelected(team)
      ? selectedTeams.filter((item) => teamKey(item) !== teamKey(team))
      : [
          ...selectedTeams,
          team.id ? { id: team.id, name: team.name } : { name: team.name },
        ];
  }

  async function addTeam() {
    if (savingTeams) return;
    const names = message
      .split(";")
      .map((name) => name.trim())
      .filter(Boolean);
    if (!names.length) return;
    savingTeams = true;
    teamSaveError = "";
    try {
      let existingTeams = projectTeams;
      if (projectId) {
        const response = await fetchWithSession(
          `/api/damprojects/dam-teams?idproject=${encodeURIComponent(projectId)}`,
        );
        if (!response.ok)
          throw new Error("Could not load project teams. Please try again.");
        existingTeams = await response.json();
      }
      for (const name of names) {
        let team = existingTeams.find(
          (item) => normalizeName(item.name) === normalizeName(name),
        ) || { name };
        if (projectId && !team.id) {
          const response = await fetchWithSession(
            "/api/damprojects/dam-teams",
            {
              method: "POST",
              body: JSON.stringify({
                name,
                idproject: projectId,
                damUsers: [],
                subprocesses: [],
              }),
            },
          );
          const result = await response.json();
          if (!response.ok || !result.ok || !result.data?.id) {
            throw new Error(
              result.message || result.error || `Could not save team ${name}.`,
            );
          }
          team = result.data;
          existingTeams = [...existingTeams, team];
        }
        if (team.id && !projectTeams.some((item) => item.id === team.id)) {
          projectTeams = [
            ...projectTeams,
            { ...team, members: team.members || team.damUsers || [] },
          ];
        }
        selectedTeams = selectedTeams.filter(
          (item) => item.id || normalizeName(item.name) !== normalizeName(name),
        );
        if (!isTeamSelected(team)) {
          selectedTeams = [
            ...selectedTeams,
            team.id ? { id: team.id, name: team.name } : { name: team.name },
          ];
        }
      }
      message = "";
    } catch (error) {
      teamSaveError =
        error.message || "Could not save teams. Please try again.";
    } finally {
      savingTeams = false;
      if (projectId) dispatch("teamsCreated");
    }
  }

  function continueAfterTeams() {
    if (savingTeams) return;
    step = 7;
    workEntryStarted = false;
    message = workDescriptionDraft;
  }

  // Ein Klick auf eine Abteilung des Projekts waehlt sie und geht weiter - ohne extra "Continue"
  function chooseDepartment(option) {
    selectedAreas = [option];
    departmentSelectionError = "";
    message = "";
    finishDepartmentSelection();
  }

  async function createDepartment() {
    if (
      confirmingDepartments ||
      loadingDepartments ||
      departmentLoadError ||
      !newDepartmentName.trim()
    )
      return;
    const names = parseNames(newDepartmentName);
    if (names.length !== 1) {
      departmentConfirmationError = "Please enter one department name.";
      return;
    }
    if (
      availableDepartments.some(
        (name) => normalizeName(name) === normalizeName(names[0]),
      )
    ) {
      departmentConfirmationError =
        "This department already exists. Select it from the list.";
      return;
    }
    const previousAreas = selectedAreas;
    selectedAreas = names;
    pendingDepartments = names;
    await confirmNewDepartments();
    if (departmentConfirmationError) {
      selectedAreas = previousAreas;
      pendingDepartments = [];
    } else {
      newDepartmentOpen = false;
    }
  }

  function cancelDepartmentChange() {
    selectedAreas = departmentBeforeEdit.selectedAreas;
    editingDepartment = false;
    departmentBeforeEdit = null;
    awaitingDepartmentConfirmation = false;
    pendingDepartments = [];
    step = 6;
    message = "";
    submitted = true;
  }

  function useWorkExample() {
    const example =
      "I check incoming invoices in SAP every day, clarify discrepancies with suppliers, and release approved invoices for payment.";
    message = message.trim() ? `${message}\n\n${example}` : example;
  }

  function getProcessSuggestions(areas) {
    const suggestions = areas.flatMap((area) => {
      const match = Object.keys(frameworkProcessSuggestions).find(
        (department) => normalizeName(department) === normalizeName(area),
      );
      return match ? frameworkProcessSuggestions[match] : [];
    });

    return Array.from(
      new Set(
        suggestions.length ? suggestions : frameworkProcessSuggestions._default,
      ),
    ).slice(0, 8);
  }

  $: allProcessSuggestions = Array.from(
    new Set(
      Object.entries(frameworkProcessSuggestions)
        .filter(([department]) => department !== "_default")
        .flatMap(([, processes]) => processes),
    ),
  );

  $: currentSavedDraft = {
    step,
    generationMode,
    started,
    selectedAreas,
    selectedL1Processes,
    frameworkDraft,
    taskMiningAssessments,
    currentL1Index,
    currentL2Index,
    currentL3Index,
    hybridL2Knowledge,
    message,
    workDescriptionDraft,
    workContext,
    pendingDepartments,
    awaitingDepartmentConfirmation,
    frameworkCreated,
    taskMiningAssessmentMode,
    selectedTeams,
    startPath,
    workEntryStarted,
  };
  $: if (!restoringDraft && started && !processingPreview)
    saveDraft(currentSavedDraft);

  function saveDraft(draft) {
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(draft));
      draftSaveMessage = "Draft saved in this browser.";
    } catch {
      draftSaveMessage =
        "Draft could not be saved. Keep this page open and try saving again.";
    }
  }

  function restoreDraft() {
    try {
      const draft = JSON.parse(localStorage.getItem(draftStorageKey) || "null");
      if (!draft) return;

      frameworkCreated = Boolean(draft.frameworkCreated);
      taskMiningAssessmentMode = false;
      step = draft.step ?? step;
      submitted = step === 6;
      generationMode = draft.generationMode || generationMode;
      started = Boolean(draft.started);
      selectedAreas = (draft.selectedAreas || []).slice(0, 1);
      selectedL1Processes = draft.selectedL1Processes || [];
      frameworkDraft = draft.frameworkDraft || [];
      taskMiningAssessments = draft.taskMiningAssessments || [];
      currentL1Index = draft.currentL1Index || 0;
      currentL2Index = draft.currentL2Index || 0;
      currentL3Index = draft.currentL3Index || 0;
      hybridL2Knowledge = draft.hybridL2Knowledge || "";
      message = draft.message || "";
      workDescriptionDraft = draft.workDescriptionDraft || "";
      startPath = startPaths.some((path) => path.id === draft.startPath)
        ? draft.startPath
        : "later";
      workEntryStarted =
        draft.workEntryStarted ?? Boolean(draft.message || draft.autoAnalyze);
      workContext = draft.workContext || {};
      pendingDepartments = draft.pendingDepartments || [];
      awaitingDepartmentConfirmation = Boolean(
        draft.awaitingDepartmentConfirmation,
      );
      autoAnalyzeText = draft.autoAnalyze || "";
      selectedTeams = draft.selectedTeams || [];
    } catch (error) {
      console.warn("Could not restore framework assistant draft:", error);
    }
  }

  function parseNames(value) {
    return Array.from(
      new Map(
        value
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean)
          .map((name) => [normalizeName(name), name]),
      ).values(),
    );
  }

  function toggleOption(option) {
    if (step === 1) {
      selectedAreas = selectedAreas[0] === option ? [] : [option];
      departmentSelectionError = "";
      message = "";
      return;
    }

    if (step === 2) {
      selectedL1Processes = selectedL1Processes.includes(option)
        ? selectedL1Processes.filter((process) => process !== option)
        : [...selectedL1Processes, option];
      message = selectedL1Processes.join(", ");
    }
  }

  function shouldAnalyzeWork() {
    return (
      step === 6 ||
      step === 7 ||
      (generationMode === "top_down" && step === 3) ||
      (generationMode === "hybrid" && step === 4)
    );
  }

  // Zu grosse Dateien gleich beim Auswaehlen melden statt erst nach dem Hochladen (Grenze wie _MAX_UPLOAD_BYTES im Backend)
  const MAX_UPLOAD_MB = 5;

  function checkFileSizes(files = []) {
    const tooLarge = files
      .filter((file) => file.size > MAX_UPLOAD_MB * 1024 * 1024)
      .map((file) => `“${file.name}”`);
    workAnalysisError = tooLarge.length
      ? `${tooLarge.join(", ")} ${tooLarge.length === 1 ? "is" : "are"} larger than ${MAX_UPLOAD_MB} MB. Please upload a smaller file or an excerpt.`
      : "";
  }

  async function analyzeWork(messageText, files) {
    analyzingWork = true;
    workAnalysisError = "";

    try {
      const formData = new FormData();
      formData.append("action", "analyze_work");
      const context =
        step === 7
          ? activityContextFields
              .map(
                (field) =>
                  `${field.label} ${workContext[field.key] || unknownAnswer}`,
              )
              .join("\n")
          : "";
      formData.append(
        "message",
        [messageText.trim(), context].filter(Boolean).join("\n\n"),
      );
      formData.append("generation_mode", generationMode);
      formData.append("areas", JSON.stringify(selectedAreas));
      formData.append("framework", JSON.stringify(frameworkDraft));
      formData.append("step", String(step));
      if (currentL1?.name) formData.append("l1_process", currentL1.name);
      if (currentL2Entry?.l2?.name)
        formData.append("l2_process", currentL2Entry.l2.name);
      files.forEach((file) => formData.append("files", file));

      const response = await fetchWithSession(
        "/api/damprojects/paxbot/framework-assistant",
        { method: "POST", body: formData },
      );
      const responseText = await response.text();
      let result = {};
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(
          "The work analysis is not available yet. Please try again later.",
        );
      }
      if (!response.ok) {
        throw new Error(
          result.message || "Could not analyze the provided information.",
        );
      }
      if (!Array.isArray(result.framework)) {
        throw new Error("The analysis did not return a framework.");
      }

      if (step === 6) {
        pendingAnalysis = result;
        analysisPreviewOpen = true;
      } else {
        rememberDraft();
        frameworkDraft = result.framework;
        taskMiningAssessments = mergeActivityReviews(
          frameworkDraft,
          result.taskMiningAssessment || [],
          taskMiningAssessments,
        );
        existingDepartmentFramework = result.departmentFramework || [];
        step = 6;
        message = "";
      }
    } catch (error) {
      workAnalysisError =
        error.message || "Could not analyze the provided information.";
    } finally {
      analyzingWork = false;
      if (workAnalysisError) submitted = false;
    }
  }

  async function handleSubmit(event) {
    if (step === 8) {
      await addTeam();
      submitted = false;
      return;
    }
    if (step === 1 && (loadingDepartments || departmentLoadError)) {
      submitted = false;
      return;
    }
    const files = event?.detail?.files || [];
    if (shouldAnalyzeWork() && (message.trim() || files.length)) {
      await analyzeWork(message, files);
      return;
    }

    if (!message.trim() && files.length) {
      dispatch("filesSubmitted", { files });
      submitted = false;
      return;
    }

    if (step === 1 && message.trim()) {
      const enteredDepartments = parseNames(message);
      if (enteredDepartments.length > 1) {
        departmentSelectionError =
          "Please select or enter one department at a time.";
        submitted = false;
        return;
      }
      departmentSelectionError = "";
      const existingDepartments = new Set(
        loadedDepartments.map((department) => normalizeName(department)),
      );
      selectedAreas = enteredDepartments;
      pendingDepartments = Array.from(
        new Map(
          enteredDepartments
            .filter(
              (department) =>
                !existingDepartments.has(normalizeName(department)),
            )
            .map((department) => [normalizeName(department), department]),
        ).values(),
      );

      if (pendingDepartments.length) {
        awaitingDepartmentConfirmation = true;
        submitted = false;
        return;
      }

      if (generationMode === "bottom_up" || editingDepartment) {
        finishDepartmentSelection();
        return;
      }

      step = 2;
      message = "";
      submitted = false;
    }

    if (step === 2 && message.trim()) {
      selectedL1Processes = parseNames(message);
      frameworkDraft = selectedL1Processes.map((name) => ({
        name,
        l2: [],
        description: "",
        status:
          generationMode === "hybrid" ? "waiting_for_description" : "draft",
      }));
      currentL1Index = 0;
      step = 3;
      message = "";
      submitted = false;
      return;
    }

    if (step === 3 && message.trim()) {
      if (generationMode === "hybrid") {
        currentL1.l2 = parseNames(message).map((name) => ({
          name,
          l3: [],
          description: "",
          status: "waiting_for_description",
        }));
        frameworkDraft = frameworkDraft;

        if (currentL1Index < frameworkDraft.length - 1) {
          currentL1Index += 1;
          hybridL2Knowledge = "";
        } else {
          currentL2Index = 0;
          step = 4;
        }
        message = "";
        submitted = false;
        return;
      }

      currentL1.l2 = parseNames(message).map((name) => ({ name, l3: [] }));
      frameworkDraft = frameworkDraft;

      if (currentL1Index < frameworkDraft.length - 1) {
        currentL1Index += 1;
      } else {
        currentL2Index = 0;
        step = 4;
      }
      message = "";
      submitted = false;
      return;
    }

    if (step === 4 && message.trim()) {
      if (generationMode === "hybrid") {
        currentL2Entry.l2.description = message.trim();
        currentL2Entry.l2.status = "description_collected";
        frameworkDraft = frameworkDraft;

        if (currentL2Index < l2Entries.length - 1) {
          currentL2Index += 1;
        } else {
          step = 6;
        }
        message = "";
        submitted = false;
        return;
      }

      currentL2Entry.l2.l3 = parseNames(message).map((name) => ({
        name,
        l4: [],
      }));
      frameworkDraft = frameworkDraft;

      if (currentL2Index < l2Entries.length - 1) {
        currentL2Index += 1;
      } else {
        currentL3Index = 0;
        step = 5;
      }
      message = "";
      submitted = false;
      return;
    }

    if (step === 5 && message.trim()) {
      currentL3Entry.l3.l4 = parseNames(message);
      frameworkDraft = frameworkDraft;

      if (currentL3Index < l3Entries.length - 1) {
        currentL3Index += 1;
      } else {
        step = 6;
      }
      message = "";
      submitted = false;
    }
  }

  async function confirmNewDepartments() {
    confirmingDepartments = true;
    departmentConfirmationError = "";

    try {
      const response = await fetchWithSession(
        "/api/damprojects/paxbot/framework-assistant",
        {
          method: "POST",
          body: JSON.stringify({
            action: "confirm_departments",
            departments: pendingDepartments,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Could not save departments");
      }

      loadedDepartments = Array.from(
        new Set([...loadedDepartments, ...pendingDepartments]),
      );
      selectedAreas = Array.from(
        new Set([...selectedAreas, ...pendingDepartments]),
      );
      departmentSelectionError = "";
      pendingDepartments = [];
      awaitingDepartmentConfirmation = false;
      finishDepartmentSelection();
    } catch (error) {
      departmentConfirmationError = error.message;
    } finally {
      confirmingDepartments = false;
    }
  }

  function editNewDepartments() {
    awaitingDepartmentConfirmation = false;
    pendingDepartments = [];
    submitted = false;
  }

  function skipHybridL2() {
    if (generationMode !== "hybrid" || step !== 3 || !currentL1) return;

    currentL1.l2 = [
      {
        name: "General work",
        l3: [],
        description: "",
        status: "waiting_for_description",
      },
    ];
    hybridL2Knowledge = "unknown";
    frameworkDraft = frameworkDraft;

    if (currentL1Index < frameworkDraft.length - 1) {
      currentL1Index += 1;
    } else {
      currentL2Index = 0;
      step = 4;
    }
    message = "";
    submitted = false;
  }

  function chooseHybridL2Knowledge(choice) {
    if (choice === "known") {
      hybridL2Knowledge = "known";
      return;
    }

    skipHybridL2();
  }

  function goBackToAreas() {
    if (editingDepartment) {
      cancelDepartmentChange();
      return;
    }
    if (step === 6) {
      step = 5;
      currentL3Index = Math.max(0, l3Entries.length - 1);
    } else if (step === 5) {
      step = 4;
      currentL2Index = Math.max(0, l2Entries.length - 1);
    } else if (step === 4) {
      step = 3;
      currentL1Index = Math.max(0, frameworkDraft.length - 1);
    } else if (step === 3) {
      step = 2;
    } else if (step === 2) {
      step = 1;
      message = selectedAreas.join(", ");
    } else if (step === 1) {
      started = false;
    } else if (step === 7) {
      workDescriptionDraft = message;
      step = 1;
      message = selectedAreas.join(", ");
    }
    if (step !== 1) message = "";
    submitted = false;
  }

  export function navigateToDepartment() {
    if (busy || frameworkCreated) return;
    if (step === 7) goBackToAreas();
    else if (step === 6) changeDepartment();
  }

  // Aus der Pruefung zurueck zum Beschreiben - der Entwurf bleibt und wird beim naechsten Beschreiben ergaenzt
  export function navigateToWork() {
    if (busy || frameworkCreated || step !== 6) return;
    rowEditorOpen = false;
    step = 7;
    workEntryStarted = true;
    message = workDescriptionDraft;
    submitted = false;
  }

  async function submitFramework() {
    if (busy || processingPreview || !frameworkComplete || !selectedAreas[0])
      return;
    creatingFramework = true;
    workAnalysisError = "";

    try {
      const rows = frameworkRows
        .filter((row) => row.l4 && row.l4 !== "—")
        .map((row) => ({
          process_l1: row.l1,
          subprocess_l2: row.l2,
          task_l3: row.l3,
          subtask_l4: row.l4,
        }));
      const response = await fetchWithSession(
        "/api/damprojects/multiple-project-presets",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rows,
            departments: selectedAreas.slice(0, 1),
          }),
        },
      );
      const result = await response.json();
      if (!response.ok || result.ok === false || result.success === false) {
        throw new Error(
          result.message || result.error || "Could not create the framework.",
        );
      }

      const detail = {
        generationMode,
        areas: selectedAreas,
        framework: frameworkDraft,
        taskMiningAssessment: taskMiningAssessments,
        result,
      };
      dispatch("frameworkSubmitted", detail);
      dispatch("processesSubmitted", detail);
      frameworkCreated = true;
      taskMiningAssessmentMode = false;
      undoHistory = [];
    } catch (error) {
      workAnalysisError = error.message || "Could not create the framework.";
    } finally {
      creatingFramework = false;
    }
  }

  function continueEditingFramework() {
    taskMiningAssessmentMode = false;
    frameworkCreated = false;
    submitted = true;
  }

  export function clearDraft() {
    if (busy) return false;
    localStorage.removeItem(draftStorageKey);
    // Prevent a queued autosave from recreating the draft before remounting.
    started = false;
    return true;
  }

  function returnToMainMenu() {
    if (!clearDraft()) return;
    frameworkCreated = false;
    taskMiningAssessmentMode = false;
    submitted = false;
    started = false;
    step = 1;
    generationMode = "bottom_up";
    undoHistory = [];
    selectedAreas = [];
    selectedL1Processes = [];
    frameworkDraft = [];
    taskMiningAssessments = [];
    existingDepartmentFramework = [];
    message = "";
    navigate(
      projectId
        ? `${ROUTES.dam.progress}?idproject=${encodeURIComponent(projectId)}`
        : ROUTES.dam.root,
    );
  }
</script>

<div
  class="framework-assistant-chat"
  class:department-step={step === 1}
  class:team-step={step === 8}
  class:work-description-step={step === 7}
  class:review-step={step === 6 &&
    (!frameworkCreated || taskMiningAssessmentMode)}
>
  <!--
    @jonas @adrian — Developer status: Review data persistence
    - Framework backend: department and L1–L4 are saved through /api/damprojects/multiple-project-presets.
    - LLM request: the free-text work description, optional context and uploaded files are sent to
      /api/damprojects/paxbot/framework-assistant to generate the hierarchy. Assessment context is also sent when
      “Suggest smaller subtasks” is used. Uploaded files are not stored by this frontend. PDFs are read by the model in the backend (up to 5 MB per file).
    - Browser only: role, trigger, tools, result, variants, suitability answers and Task Mining scope selection remain
      in paxray.framework-assistant.draft.
    - Project backend: project name, department and contacts are sent to /api/damprojects/projects; the project
      description is currently sent empty. Assessment data is not copied into it.
    - Backend to do: persist structured assessment data and restore it when the assistant is reopened.
  -->
  <!--
    @jonas @adrian — Why the optional Task Mining questions exist
    The optional context can guide hierarchy generation. The suitability answers do not change Process (L1) through
    Subtask (L4); they prepare a later Task Mining setup:
    - Role: identifies the people who can participate in the recording.
    - Start: defines when the recorder should begin observing the subtask.
    - Applications: identifies which systems belong in the recording scope.
    - Finish: defines the completion boundary and expected output.
    - Exceptions or handoffs: shows where to split the work or exclude another person’s work.
    - Mainly digital: checks whether the work can technically be captured.
    - Continuous session: checks whether the subtask is one recordable unit or needs splitting.
    - Frequency: checks whether enough comparable executions are likely during the project.
    - Consistency: checks whether repeated recordings can produce a useful common pattern.
  -->
  <PaxbotFullscreenChat
    {moveToBottomOnSubmit}
    darkStartScreen={true}
    submitLabel={step === 8
      ? "Add teams"
      : step === 1
        ? "Continue"
        : step === 7
          ? "Analyze work"
          : ""}
    submitDisabled={busy ||
      savingTeams ||
      (step === 1 && (loadingDepartments || Boolean(departmentLoadError)))}
    showNotice={false}
    workInProgressNotice={true}
    showComposer={((step > 0 &&
      step < 6 &&
      !awaitingDepartmentConfirmation &&
      !(step === 3 && generationMode === "hybrid" && !hybridL2Knowledge)) ||
      (step === 6 && !frameworkCreated) ||
      step === 7 ||
      step === 8) &&
      !analyzingWork &&
      step !== 1 &&
      !(step === 7 && (!workEntryStarted || startPath === "later"))}
    centerContent={step === 8 ||
      step === 7 ||
      (step === 6 && frameworkCreated && !taskMiningAssessmentMode)}
    showBackButton={false}
    processing={analyzingWork || creatingFramework}
    showGreetingWhenInput={step === 6 || step === 8 || step === 7}
    processingTitle={creatingFramework
      ? "Adding to your framework"
      : "Processing your input and preparing your addition"}
    processingMessage="This can take a moment. Please wait."
    accept=".pdf,.docx,.txt,.md,.csv,.json,.xml,.bpmn,.png,.jpg,.jpeg"
    allowFileUpload={step === 6 ||
      (generationMode === "bottom_up" && step === 7) ||
      (generationMode === "top_down" && step === 3) ||
      (generationMode === "hybrid" && step === 4)}
    multiple={step === 6 ||
      (generationMode === "bottom_up" && step === 7) ||
      (generationMode === "top_down" && step === 3) ||
      (generationMode === "hybrid" && step === 4)}
    bind:started
    bind:message
    noticeTitle="Framework Assistant"
    noticeMessage="Use Pax AI to build your process framework step by step. Choose one department, describe the work performed there, and review the generated framework before confirming it."
    createLabel="Build with Framework Assistant"
    showImport={false}
    greeting={currentGreeting}
    startGreeting="How would you like to work with your framework?"
    startSubtitle="Create a new framework or extend your current framework with Pax AI."
    placeholder={step === 8
      ? "Enter team names separated by semicolons, e.g. Finance; Sales; Support"
      : step === 1
        ? "Enter a department, e.g. Finance"
        : step === 2
          ? "Enter processes, separated by commas, e.g. Procure-to-Pay, Record-to-Report"
          : step === 3
            ? generationMode === "hybrid"
              ? "Enter subprocesses, separated by commas, e.g. Invoice review, Payment approval"
              : "Describe the work steps and how they belong together"
            : step === 4
              ? generationMode === "hybrid"
                ? "Describe the work steps in this subprocess"
                : "Enter tasks, separated by commas, e.g. Check invoice, Resolve discrepancy"
              : step === 5
                ? "Enter subtasks, separated by commas, e.g. Open invoice, Compare amount, Approve"
                : step === 7 && generationMode === "bottom_up"
                  ? startPath === "describe"
                    ? "Describe what the team does on a normal day"
                    : "Upload a file with +, then click the arrow to start"
                  : step === 6
                    ? "Describe what to add, remove, rename or split"
                    : "Add details to improve this framework"}
    bind:submitted
    on:submit={handleSubmit}
    on:back={goBackToAreas}
    on:filesSelected={(event) => {
      checkFileSizes(event.detail.files);
      dispatch("filesSelected", event.detail);
    }}
  >
    <div slot="suggestions" class="framework-area-suggestions">
      {#if step === 6}
        {#if frameworkCreated && !taskMiningAssessmentMode}
          <p
            class="framework-area-helper framework-result-helper framework-success-helper"
          >
            Your framework addition is saved. You can edit it again or return to
            {projectId ? "the project" : "the main menu"}.
          </p>
          <div
            class="framework-area-actions framework-result-actions framework-saved-actions"
          >
            <Button
              size="field"
              kind="paxAI"
              text="Edit framework"
              iconLeft={true}
              icon={PLUS_ICON}
              on:click={continueEditingFramework}
            />
            <Button
              size="field"
              kind="paxAI"
              text={projectId ? "Back to project" : "Back to main menu"}
              iconLeft={true}
              icon={projectId ? ARROW_LEFT_ICON : HOME_ICON}
              on:click={returnToMainMenu}
            />
          </div>
        {:else}
          <div class="review-meta">
            <p class="framework-area-helper framework-result-helper">
              {taskMiningAssessmentMode
                ? "Optionally assess which subtasks are suitable for Task Mining. Subtasks marked as not suitable are not removed automatically. For a Task Mining-focused framework, we recommend removing them before saving. These answers currently remain in this browser."
                : "Check the Process (L1), Subprocess (L2), Task (L3) and Subtask (L4) hierarchy before saving. Tasks that are not suitable for Task Mining should not be included in the framework."}
            </p>
            {#if workAnalysisError}
              <p class="framework-area-error">{workAnalysisError}</p>
            {/if}
            {#if taskMiningAssessmentMode}<section
                class="pilot-readiness"
                class:ready={pilotReady}
                aria-label="Task Mining readiness"
              >
                <div class="readiness-summary">
                  <span class="readiness-dot" aria-hidden="true"></span>
                  <div>
                    <strong
                      >{pilotReady
                        ? "Ready for project setup"
                        : !pilotRows.length
                          ? "Choose subtasks for Task Mining"
                          : `${pilotOpenRows.length} ${pilotOpenRows.length === 1 ? "subtask needs" : "subtasks need"} review`}</strong
                    >
                    <p>
                      {pilotRows.length} selected · {frameworkRows.length -
                        pilotRows.length} outside Task Mining scope
                    </p>
                  </div>
                </div>
                {#if pilotOpenRows.length}
                  <Button
                    text="Review next subtask"
                    size="field"
                    kind="secondary"
                    on:click={() => openTaskMiningReview(pilotOpenRows[0])}
                  />
                {/if}
              </section>{/if}
            <div class="review-toolbar">
              <div class="review-controls">
                <label
                  ><span class="visually-hidden">Search</span><input
                    type="search"
                    bind:value={reviewSearch}
                    placeholder="Search processes or subtasks"
                  /></label
                >
                <label
                  ><span class="visually-hidden">Show</span><select
                    bind:value={reviewFilter}
                    ><option value="all">All subtasks</option
                    >{#if taskMiningAssessmentMode}<option value="clarification"
                        >Open suitability questions</option
                      >{:else}<option value="incomplete"
                        >Missing hierarchy fields</option
                      >{/if}</select
                  ></label
                >
                {#if !taskMiningAssessmentMode}
                  <Button
                    text="Add Task"
                    size="field"
                    kind="paxAI"
                    on:click={() => openRowEditor("add")}
                  />
                {/if}
              </div>
            </div>
          </div>
          <!-- Keyboard focus allows horizontal scrolling without a mouse. -->
          <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
          <div
            class="framework-table-wrap"
            tabindex="0"
            role="region"
            aria-label="Framework subtasks, scroll horizontally to see all columns"
          >
            <table class="framework-table" bind:this={reviewTable}>
              <colgroup>
                <col class="framework-col-l1" />
                <col class="framework-col-l2" />
                <col class="framework-col-l3" />
                <col class="framework-col-l4" />
                {#if taskMiningAssessmentMode}<col
                    class="framework-col-fit"
                  />{:else}<col class="framework-col-tm" /><col
                    class="framework-col-delete"
                  />{/if}
              </colgroup>
              <thead>
                <tr
                  ><th>Process (L1)</th><th>Subprocess (L2)</th><th
                    >Task (L3)</th
                  ><th>Subtask (L4)</th>{#if taskMiningAssessmentMode}<th
                      >Task Mining assessment</th
                    >{:else}<th
                      ><span class="tm-fit-header"
                        ><span class="tm-fit-label">TM Fit</span><Tooltip
                          ><div class="tm-fit-tooltip-content">
                            <strong>Task Mining fit</strong>
                            <p>
                              Optional check whether a subtask is suitable for
                              Task Mining.
                            </p>
                            <p>
                              The status comes from four questions:<br />Is the
                              work mainly digital?<br />Can one person do it in
                              one session?<br />How often is it done?<br />How
                              consistent is it?
                            </p>
                            <p>
                              Yellow means an answer is missing or needs a
                              closer look.<br />Click the status to see what to
                              do next.
                            </p>
                            <ul>
                              <li>
                                <span class="tm-tooltip-status suitable">✓</span
                                ><span><strong>Green:</strong> Suitable</span>
                              </li>
                              <li>
                                <span class="tm-tooltip-status uncertain"
                                  >?</span
                                ><span
                                  ><strong>Yellow:</strong> Needs clarification</span
                                >
                              </li>
                              <li>
                                <span class="tm-tooltip-status unsuitable"
                                  >×</span
                                ><span><strong>Red:</strong> Not suitable</span>
                              </li>
                            </ul>
                          </div></Tooltip
                        ></span
                      ></th
                    ><th>Actions</th>{/if}</tr
                >
              </thead>
              <tbody>
                {#each processGroups as group}
                  <tr class="process-group"
                    ><th
                      colspan={taskMiningAssessmentMode ? 5 : 6}
                      scope="rowgroup"
                      ><button
                        type="button"
                        aria-expanded={!collapsedProcesses.includes(group.name)}
                        on:click={() => toggleProcess(group.name)}
                        ><img
                          class:collapsed={collapsedProcesses.includes(
                            group.name,
                          )}
                          class="process-chevron"
                          src="/img/assistant/icons/chevron_down.svg"
                          alt=""
                        />{group.name || "Unnamed process"}
                        <span
                          >({group.rows.length}
                          {group.rows.length === 1
                            ? "subtask"
                            : "subtasks"})</span
                        ></button
                      ></th
                    ></tr
                  >
                  {#if !collapsedProcesses.includes(group.name)}
                    {#each group.rows as row}
                      <tr data-row-key={row.rowKey}>
                        <td data-level="l1"
                          ><div class="framework-cell">
                            {#if row.l1Missing}<BadgeColor
                                text="Required"
                                color="red"
                                small
                              />{/if}<PaxbotInlineEditableCell
                              disabled={taskMiningAssessmentMode}
                              open={activeInlineCell === `${row.rowKey}-l1`}
                              value={row.l1}
                              placeholder="Name process"
                              options={[]}
                              renameOnly
                              label="Process (L1)"
                              on:open={() =>
                                (activeInlineCell = `${row.rowKey}-l1`)}
                              on:change={(event) =>
                                handleInlineChange(row, "l1", event)}
                            />
                          </div></td
                        >
                        <td data-level="l2"
                          ><div class="framework-cell">
                            {#if row.l2Missing}<BadgeColor
                                text="Required"
                                color="red"
                                small
                              />{/if}<PaxbotInlineEditableCell
                              disabled={taskMiningAssessmentMode}
                              open={activeInlineCell === `${row.rowKey}-l2`}
                              value={row.l2}
                              placeholder="Name subprocess"
                              options={[]}
                              renameOnly
                              label="Subprocess (L2)"
                              on:open={() =>
                                (activeInlineCell = `${row.rowKey}-l2`)}
                              on:change={(event) =>
                                handleInlineChange(row, "l2", event)}
                            />
                          </div></td
                        >
                        <td data-level="l3"
                          ><div class="framework-cell">
                            {#if row.l3Missing}<BadgeColor
                                text="Required"
                                color="red"
                                small
                              />{/if}<PaxbotInlineEditableCell
                              disabled={taskMiningAssessmentMode}
                              open={activeInlineCell === `${row.rowKey}-l3`}
                              value={row.l3}
                              placeholder="Name task"
                              options={[]}
                              renameOnly
                              label="Task (L3)"
                              on:open={() =>
                                (activeInlineCell = `${row.rowKey}-l3`)}
                              on:change={(event) =>
                                handleInlineChange(row, "l3", event)}
                            />
                          </div></td
                        >
                        <td data-level="l4"
                          ><div class="framework-cell">
                            {#if row.l4Missing}<BadgeColor
                                text="Required"
                                color="red"
                                small
                              />{/if}<PaxbotInlineEditableCell
                              disabled={taskMiningAssessmentMode}
                              open={activeInlineCell === `${row.rowKey}-l4`}
                              value={row.l4}
                              placeholder="Name subtask"
                              options={[]}
                              renameOnly
                              label="Subtask (L4)"
                              on:open={() =>
                                (activeInlineCell = `${row.rowKey}-l4`)}
                              on:change={(event) =>
                                handleInlineChange(row, "l4", event)}
                            />
                          </div></td
                        >
                        {#if taskMiningAssessmentMode}<td>
                            <div class="task-mining-fit">
                              <button
                                class="fit-badge-button"
                                type="button"
                                aria-label={`Review subtask details and suitability for ${row.l4}`}
                                title={row.fitReason}
                                on:click={() => openTaskMiningReview(row)}
                              >
                                <BadgeColor
                                  text={row.fitStatus === "not_suitable"
                                    ? "Outside digital scope"
                                    : activityOpenItems(row).length
                                      ? "Review subtask →"
                                      : "Ready · View"}
                                  color={row.fitStatus === "not_suitable"
                                    ? "grey"
                                    : activityOpenItems(row).length
                                      ? "yellow"
                                      : "green"}
                                  small
                                />
                              </button>
                              <label class="pilot-selection"
                                ><input
                                  type="checkbox"
                                  checked={row.included}
                                  on:change={() => togglePilotActivity(row)}
                                /> Include in Task Mining scope</label
                              >
                            </div>
                          </td>{:else}<td class="tm-fit-cell">
                            <button
                              class="tm-fit-button"
                              class:suitable={row.fitStatus === "suitable" &&
                                !activityOpenItems(row).length}
                              class:notSuitable={row.fitStatus ===
                                "not_suitable"}
                              type="button"
                              aria-label={`Optional Task Mining assessment for ${row.l4 || "unnamed subtask"}: ${taskMiningFitLabel(row.fitStatus)}`}
                              title={`${taskMiningFitLabel(row.fitStatus)}. ${row.fitReason}`}
                              on:click={() => openTaskMiningReview(row)}
                            >
                              <span aria-hidden="true"
                                >{row.fitStatus === "not_suitable"
                                  ? "×"
                                  : row.fitStatus === "suitable" &&
                                      !activityOpenItems(row).length
                                    ? "✓"
                                    : "?"}</span
                              >
                            </button>
                          </td><td>
                            <div
                              class="framework-row-actions"
                              aria-label={`Actions for ${row.l4 || "unnamed subtask"}`}
                            >
                              <PaxbotTableActionButton
                                icon="copy_dark.svg"
                                title={`Duplicate ${row.l4 || "subtask"}`}
                                on:click={() => openRowEditor("duplicate", row)}
                              />
                              <PaxbotTableActionButton
                                icon="compare_arrows_dark.svg"
                                title={`Move ${row.l4 || "subtask"}`}
                                on:click={() => openRowEditor("move", row)}
                              />
                              <PaxbotTableActionButton
                                icon="merge.svg"
                                title={`Merge ${row.l4 || "subtask"}`}
                                disabled={frameworkRows.length < 2}
                                on:click={() => openRowEditor("merge", row)}
                              />
                              <PaxbotTableActionButton
                                icon="trash.svg"
                                title={`Delete ${row.l4 || "subtask"}`}
                                danger
                                on:click={() => askRemovePreviewRow(row)}
                              />
                            </div>
                          </td>{/if}
                      </tr>
                    {/each}
                  {/if}
                {:else}
                  <tr
                    ><td colspan={taskMiningAssessmentMode ? 5 : 6}
                      >{frameworkRows.length
                        ? "No subtasks match these filters."
                        : "No subtasks yet. Add a subtask or describe more work below."}</td
                    ></tr
                  >
                {/each}
              </tbody>
            </table>
          </div>
          <div
            class="framework-area-actions framework-result-actions review-footer"
          >
            <div class="draft-status" role="status">
              {draftSaveMessage}
              {#if draftSaveMessage.includes("could not")}<button
                  type="button"
                  disabled={busy || processingPreview}
                  on:click={() => saveDraft(currentSavedDraft)}
                  >Retry saving</button
                >{/if}
            </div>
            {#if taskMiningAssessmentMode}
              <Button
                size="field"
                text="Finish assessment"
                kind="secondary"
                on:click={closeTaskMiningAssessment}
              />
            {:else}
              {#if missingCells.length}
              <Button
                  size="field"
                  text={`Complete ${missingCells.length} missing fields`}
                  kind="secondary"
                  on:click={focusNextMissingCell}
                />
                {/if}
              <span
                class="framework-confirm-button"
                title={processingPreview
                  ? "Preview mode: confirming is disabled."
                  : frameworkComplete
                    ? ""
                    : "The addition is not complete yet. Please fill in all required fields."}
              >
                <Button
                  size="field"
                  kind="paxAI"
                  text="Save framework"
                  disabled={processingPreview ||
                    !frameworkComplete ||
                    analyzingWork ||
                    creatingFramework}
                  on:click={submitFramework}
                />
              </span>
            {/if}
          </div>
        {/if}
      {:else if step === 7}
        <!-- Gefuehrter Einstieg: vier Wege, kombinierbar - Team-Umfrage ist die Voreinstellung -->
        {#if !workEntryStarted}
          <div
            class="start-paths"
            role="radiogroup"
            aria-label="How do you want to start?"
          >
            {#each startPaths as path}
              <button
                type="button"
                role="radio"
                aria-checked={startPath === path.id}
                class:active={startPath === path.id}
                on:click={() => (startPath = path.id)}
              >
                <strong>{path.title}</strong><span>{path.text}</span>
              </button>
            {/each}
          </div>
          <div class="framework-area-actions">
            <Button
              size="field"
              kind="paxAI"
              text="Continue"
              on:click={() => {
                workEntryStarted = true;
                submitted = false;
              }}
            />
          </div>
        {:else}
          {#if startPath === "later"}
            <div class="start-path-detail">
              <p>
                Employees of the department list their tasks in a short survey
                (about 15 minutes). Pax AI merges the answers into one list. You
                build the framework from it.
              </p>
              {#if !projectId}
                <!-- Ohne Projekt keine Teams: statt der Sackgasse das Projekt waehlen lassen (Rueckmeldung 05./07.10.) -->
                {#if surveyProjects.length}
                  <p>
                    A team survey belongs to the teams of a project. Choose the
                    project:
                  </p>
                  <div class="survey-projects">
                    {#each surveyProjects as project (project.id)}
                      <Button
                        size="field"
                        kind="paxAI"
                        text={project.name}
                        on:click={() => openInProject(project.id)}
                      />
                    {/each}
                  </div>
                {:else}
                  <p>
                    Open the assistant from a project's Configure Framework to
                    set up a team survey.
                  </p>
                {/if}
              {:else}
                <TeamSurveySetup
                  {projectId}
                  teams={surveyTeams}
                  preselected={selectedTeams}
                  department={selectedAreas[0] || ""}
                  on:created={(event) =>
                    dispatch("surveyCreated", event.detail)}
                  on:teamsCreated={() => dispatch("teamsCreated")}
                  on:frameworkChanged={() => dispatch("frameworkChanged")}
                  on:manage={() => dispatch("teamSurvey")}
                />
              {/if}
            </div>
          {:else if startPath === "framework"}
            <div class="start-path-detail">
              <p>
                Upload your existing framework using the + button below. You can
                use CSV, PDF or BPMN files. Pax AI keeps its structure for you
                to review before saving.
              </p>
              <p class="start-path-note">
                Your existing framework in paxray appears on the right. Continue
                with what is missing.
              </p>
            </div>
          {:else if startPath !== "describe"}
            <p class="framework-area-helper">
              Upload process diagrams (BPMN), work instructions (PDF, DOCX,
              TXT) or screenshots (PNG, JPG) with the + button below. Then click the arrow to start the
              analysis. Pax AI builds a proposal from your files, and you review
              it before anything is saved.
            </p>
          {:else if projectId}
            <div class="start-path-detail">
              <p>
                Just start writing below. A few sentences in your own words are
                enough, no structure needed. Pax AI turns them into a first
                framework.
              </p>
              <p class="start-path-note">
                Nothing has to be complete: you can add more at any time, step
                by step, together with Pax AI.
              </p>
              <!-- Als Tooltip statt Aufklapptext, damit das Eingabefeld nicht springt -->
              <span class="task-hint"
                ><Tooltip triggerText="What is a task?" direction="bottom"
                  ><div class="task-hint-tooltip-content">
                    <strong>A task is one piece of work with a clear result.</strong>
                    <p>
                      Usually one person does it in one go, from trigger to
                      outcome.
                    </p>
                    <p>
                      Examples:<br />Match an incoming invoice with its purchase
                      order<br />Create a customer in the CRM<br />Answer a
                      delivery inquiry
                    </p>
                    <p>Smaller than a process, bigger than a single click.</p>
                  </div></Tooltip
                ></span
              >
            </div>
          {:else}
            <p class="framework-area-helper">
              Just start writing below in your own words, with no structure
              needed, or add supporting files (PDF, DOCX, TXT, MD, CSV, JSON,
              XML, BPMN, PNG or JPG). Pax AI turns it into a first framework, and you can
              add more at any time, step by step.
            </p>
            <div class="work-description-panels">
              <div class="work-guidance">
                <strong>Describe the work in your own words</strong>
                <ul class="work-guidance-prompts">
                  <li>
                    <span>Start</span><strong>Where does the work begin?</strong
                    >
                  </li>
                  <li>
                    <span>Flow</span><strong>Which work steps follow?</strong>
                  </li>
                  <li>
                    <span>Scope</span><strong>What belongs together?</strong>
                  </li>
                  <li>
                    <span>End</span><strong>Where does the work finish?</strong>
                  </li>
                </ul>
                <p class="work-guidance-example">
                  <span>Example:</span> “I check incoming invoices in SAP every day,
                  clarify discrepancies with suppliers, and release approved invoices
                  for payment.”
                </p>
              </div>
            </div>
          {/if}
          {#if workAnalysisError}
            <p class="framework-area-error">{workAnalysisError}</p>
          {/if}
        {/if}
      {:else if step === 8}
        <!-- Gefuehrt: welche Teams arbeiten in der Abteilung - mehrere moeglich, optional -->
        <p class="framework-area-helper">
          {#if projectId}Teams created here belong to this project and are not
            permanently linked to the selected department.
          {/if}
          Optional: each team can later list its tasks in a short survey.
        </p>
        {#if teamSaveError}<p class="framework-area-error">
            {teamSaveError}
          </p>{/if}
        {#if projectTeams.length || selectedTeams.some((team) => !team.id)}
          <div class="team-tags">
            {#each [...projectTeams, ...selectedTeams.filter((team) => !team.id)] as team}
              <button
                type="button"
                class="team-tag"
                class:selected={isTeamSelected(team)}
                aria-pressed={isTeamSelected(team)}
                aria-label={isTeamSelected(team)
                  ? `Remove ${team.name} from selection`
                  : `Select ${team.name}`}
                on:click={() => toggleTeam(team)}
              >
                {team.name}
                {#if isTeamSelected(team)}<img
                    src="/img/assistant/icons/close_dark.svg"
                    alt=""
                    aria-hidden="true"
                  />{/if}
              </button>
            {/each}
          </div>
        {/if}
      {:else if awaitingDepartmentConfirmation}
        <p class="framework-area-helper">
          We could not find this department: {pendingDepartments.join(", ")}.
          Would you like to add it and continue?
        </p>
        {#if departmentConfirmationError}
          <p class="framework-area-error">{departmentConfirmationError}</p>
        {/if}
        <div class="framework-area-actions">
          <Button
            size="field"
            kind="paxAI"
            text="Add department and continue"
            disabled={confirmingDepartments}
            on:click={confirmNewDepartments}
          />
          <Button
            size="field"
            kind="paxAI"
            text="Change department"
            on:click={editNewDepartments}
          />
        </div>
      {:else}
        {#if step !== 1}<p class="framework-area-helper">
            {step === 2
              ? "Tell us what you want to analyze or add. Use the suggestions, add to them, or write freely."
              : step === 3
                ? generationMode === "hybrid"
                  ? hybridL2Knowledge
                    ? "Add the subprocesses you know, or write them freely below."
                    : "Do you already know some subprocesses for this area?"
                  : "Tell us what you do every day as part of this process. Describe it in your own words or upload work instructions, BPMN models, or free-text notes. We will derive the relevant tasks and subtasks from this information."
                : step === 4
                  ? generationMode === "hybrid"
                    ? "Tell us what you do every day in this subprocess. Describe it in your own words or upload work instructions, BPMN models, or free-text notes. We will derive the relevant tasks and subtasks from this information."
                    : "Add more detail about this part of the process. Use the previous text as a starting point, add to it, or write freely."
                  : "List the individual subtasks involved. Use the previous text as a starting point, add to it, or write freely."}
          </p>{/if}
        {#if workAnalysisError}
          <p class="framework-area-error">{workAnalysisError}</p>
        {/if}
        {#if departmentSelectionError}
          <p class="framework-area-error">{departmentSelectionError}</p>
        {/if}
        {#if step === 1 && loadingDepartments}
          <p class="framework-area-helper" role="status">
            Loading departments…
          </p>
        {:else if step === 1 && departmentLoadError}
          <p class="framework-area-error" role="alert">{departmentLoadError}</p>
          <button type="button" on:click={loadDepartments}>Retry</button>
        {/if}
        {#if step === 1}
          <div class="department-choice-row">
            <div class="choice-list">
              {#if !loadingDepartments && !departmentLoadError}
                <PaxAiDepartmentPopover
                  selector
                  departments={availableDepartments}
                  selectedDepartments={selectedAreas}
                  on:toggle={(event) => chooseDepartment(event.detail)}
                />
              {/if}
            </div>
            <Button
              kind="paxAI"
              size="field"
              text="New department"
              disabled={loadingDepartments || Boolean(departmentLoadError)}
              on:click={() => {
                newDepartmentName = "";
                departmentConfirmationError = "";
                newDepartmentOpen = true;
              }}
            />
          </div>
        {:else}
          {#each currentOptions as option}
            <button
              type="button"
              class:selected={selectedL1Processes.includes(option)}
              aria-pressed={selectedL1Processes.includes(option)}
              on:click={() => toggleOption(option)}
            >
              {option}
            </button>
          {/each}
        {/if}
        {#if step === 1 && editingDepartment}<button
            type="button"
            on:click={cancelDepartmentChange}>Cancel department change</button
          >{/if}
        {#if step === 2}
          <PaxAiDepartmentPopover
            items={allProcessSuggestions}
            selectedItems={selectedL1Processes}
            searchPlaceholder="Search processes"
            on:toggle={(event) => toggleOption(event.detail)}
          />
        {/if}
        {#if step === 3 && generationMode === "hybrid"}
          <div class="knowledge-options">
            <Button
              size="field"
              kind="paxAI"
              text="Yes, I know some"
              on:click={() => chooseHybridL2Knowledge("known")}
            />
            <Button
              size="field"
              kind="paxAI"
              text="No, not yet"
              on:click={() => chooseHybridL2Knowledge("unknown")}
            />
          </div>
        {/if}
      {/if}
    </div>
    <svelte:fragment slot="afterComposer">
      {#if step === 7 && workEntryStarted && !busy}
        <div class="framework-area-actions work-entry-back">
          <Button
            size="field"
            kind="paxAI"
            text="Back"
            iconLeft
            icon={ARROW_LEFT_ICON}
            on:click={() => {
              workEntryStarted = false;
              workAnalysisError = "";
              submitted = false;
            }}
          />
        </div>
      {/if}
      {#if step === 8}
        <div class="team-continue">
          <Button
            size="field"
            kind="paxAI"
            text={selectedTeams.length
              ? `Continue with ${selectedTeams.length} ${selectedTeams.length === 1 ? "team" : "teams"}`
              : "Continue without teams"}
            disabled={savingTeams}
            on:click={continueAfterTeams}
          />
        </div>
      {/if}
    </svelte:fragment>
  </PaxbotFullscreenChat>
</div>

<Modal
  bind:open={newDepartmentOpen}
  modalHeading="New department"
  modalClass="framework-new-department-modal"
  autowidth
  primaryButtonText={confirmingDepartments ? "Creating …" : "Create department"}
  primaryButtonVariant="secondary"
  secondaryButtonText="Cancel"
  primaryButtonValidations={confirmingDepartments}
  primaryButtonNotAllow={!newDepartmentName.trim() ||
    loadingDepartments ||
    Boolean(departmentLoadError)}
  primaryButtonAction={createDepartment}
  actions
>
  <TextInput
    labelText="Department name"
    placeholder="e.g. Finance"
    bind:value={newDepartmentName}
    disabled={confirmingDepartments}
  />
  {#if departmentConfirmationError}<p class="new-department-error" role="alert">
      {departmentConfirmationError}
    </p>{/if}
</Modal>

<Modal
  bind:open={analysisPreviewOpen}
  modalHeading="Review AI changes"
  primaryButtonText="Apply changes"
  secondaryButtonText="Keep current draft"
  actions
  width={760}
  primaryButtonAction={acceptAnalysis}
  secondaryButtonAction={discardAnalysis}
>
  <div class="analysis-preview">
    <p>
      {analysisChanges.added.length} added · {analysisChanges.removed.length} removed.
      Renamed or moved subtasks appear as a removal and an addition. Your current
      draft stays unchanged until you apply this proposal.
    </p>
    {#if !analysisChanges.added.length && !analysisChanges.removed.length}<p>
        The hierarchy is unchanged. Applying this proposal may update Task
        Mining assessments.
      </p>{/if}
    <div class="analysis-diff">
      {#each [{ label: "Removed from current draft", rows: analysisChanges.removed }, { label: "Added in proposed draft", rows: analysisChanges.added }] as section}
        <section>
          <h4>{section.label}</h4>
          <ul>
            {#each section.rows as row}<li>
                {hierarchyLevels
                  .map((level) => row[level] || "Missing")
                  .join(" → ")}
              </li>{:else}<li>None</li>{/each}
          </ul>
        </section>
      {/each}
    </div>
    <p>You can undo an applied proposal during this editing session.</p>
  </div>
</Modal>

<Modal
  bind:open={rowEditorOpen}
  modalHeading={rowEditorMode === "merge"
    ? "Merge subtask"
    : rowEditorMode === "move"
      ? "Move subtask"
      : rowEditorMode === "duplicate"
        ? "Duplicate Task"
        : "Add Task"}
  primaryButtonText={rowEditorMode === "merge"
    ? "Merge subtasks"
    : rowEditorMode === "move"
      ? "Move subtask"
      : rowEditorMode === "duplicate"
        ? "Duplicate Task"
        : "Add Task"}
  secondaryButtonText="Cancel"
  actions
  modalClass="framework-row-editor-modal"
  autowidth
  primaryButtonValidations={!rowEditorValid}
  primaryButtonAction={applyRowEditor}
>
  <div class="row-editor" class:hierarchy-editor={rowEditorMode !== "merge"}>
    {#if rowEditorMode === "merge"}
      <p>
        Merge “{rowEditorSource?.l4 || "Unnamed subtask"}” into another subtask.
        The target name and hierarchy are kept; the source row is removed. Task
        Mining suitability must be reassessed. You can undo this change.
      </p>
      <label
        >Keep this subtask
        <select bind:value={mergeTargetKey}>
          <option value="">Choose target subtask</option>
          {#each frameworkRows.filter((row) => row.rowKey !== rowEditorSource?.rowKey) as row}
            <option value={row.rowKey}
              >{hierarchyLevels
                .map((level) => row[level] || "Missing")
                .join(" → ")}</option
            >
          {/each}
        </select>
      </label>
    {:else}
      {#if rowEditorMode === "move"}<p>
          Choose the destination for this subtask. Other subtasks remain in
          their current branch. Use inline editing to rename a branch.
        </p>{/if}
      {#each hierarchyLevels as level}
        <label
          >{hierarchyLabels[level]}
          <input
            bind:value={rowEditorDraft[level]}
            placeholder={hierarchyPlaceholders[level]}
            list={`framework-destination-${level}`}
            readonly={rowEditorMode === "move" && level === "l4"}
          />
          <datalist id={`framework-destination-${level}`}>
            {#each getInlineOptions(rowEditorDraft, level) as option}<option
                value={option}
              />{/each}
          </datalist>
        </label>
      {/each}
    {/if}
    {#if rowEditorError}<p role="alert" class="framework-area-error">
        {rowEditorError}
      </p>{/if}
  </div>
</Modal>

<Modal
  bind:open={fitReviewModalOpen}
  modalHeading={fitReviewRow
    ? `Review ${fitReviewRow.l4}`
    : "Review Task Mining suitability"}
  primaryButtonText={fitReviewKnockout
    ? "Save as not suitable"
    : "Save assessment"}
  primaryButtonVariant="secondary"
  secondaryButtonText="Save for later"
  noSecondaryButton={!fitReviewKnockout && fitReviewQuestionsComplete}
  actions
  autowidth
  modalClass="fit-review-modal"
  preventCloseOnClickOutside
  primaryButtonValidations={busy ||
    (!fitReviewKnockout && !fitReviewQuestionsComplete)}
  primaryButtonAction={saveTaskMiningReview}
  secondaryButtonAction={saveTaskMiningReviewForLater}
>
  {#if fitReviewRow}
    <div class="fit-review">
      <p class="activity-breadcrumb">
        {[fitReviewRow.l1, fitReviewRow.l2, fitReviewRow.l3].join(" / ")}
      </p>
      <!-- Subtask-Details (Rolle, Start, Anwendungen ...) vorerst ausgeblendet: sie werden beim Speichern nicht ans Backend uebertragen -->
      <InfoBlock
        type="info"
        heading="Check or complete the answers below"
        message="If they are correct, click Save assessment. If not, change them and the result updates."
        showClose={false}
      />
      <div class="fit-review-questions">
          {#each fitReviewRow.fitQuestions as question}
            <fieldset>
              <legend>{question.question}</legend>
              <div class="fit-review-options">
                {#each question.options as option}
                  <button
                    type="button"
                    class:selected={fitReviewAnswers[question.id] === option}
                    class:unknown-option={option === unknownAnswer}
                    aria-pressed={fitReviewAnswers[question.id] === option}
                    on:click={() =>
                      answerTaskMiningQuestion(question.id, option)}
                  >
                    {question.labels?.[option] ?? option}
                  </button>
                {/each}
              </div>
            </fieldset>
          {/each}
        </div>
        {#if fitReviewNeedsSplit}
          <div class="activity-split-help">
            <InfoBlock
              type="warning"
              heading="Not suitable as one subtask"
              message="Task Mining collects data while one person works through a subtask in one go. Handoffs or waiting interrupt the data collection. Split the subtask at these points so data can be collected for each part on its own. You can review the suggested subtasks before they are added. If you save it as not suitable, it stays in the table but is excluded from the framework."
              showClose={false}
            />
            <Button
              text="Suggest smaller subtasks"
              kind="secondary"
              size="field"
              disabled={busy || processingPreview}
              on:click={suggestActivitySplit}
            />
          </div>
        {:else if fitReviewNotice}
          <InfoBlock
            type={fitReviewNotice.type}
            heading={fitReviewNotice.heading}
            message={fitReviewResolution.reason}
            showClose={false}
          />
        {/if}
    </div>
  {/if}
</Modal>

<Modal
  bind:open={deleteModalOpen}
  modalHeading="Remove row from preview?"
  primaryButtonText="Remove row"
  secondaryButtonText="Cancel"
  kind="danger"
  actions
  autowidth
  preventCloseOnClickOutside
  primaryButtonAction={removePreviewRow}
>
  This will remove the selected row from the preview. It will not be added when
  you confirm the addition. Do you want to continue?
</Modal>

<Modal
  bind:open={inlineChangeModalOpen}
  modalHeading="Apply this change to matching rows?"
  primaryButtonText="Change all matching rows"
  secondaryButtonText="Only this row"
  secondaryButtonRight
  actions
  autowidth
  preventCloseOnClickOutside
  primaryButtonAction={() => applyPendingInlineChange(true)}
  secondaryButtonAction={() => applyPendingInlineChange(false)}
>
  This name appears in {pendingInlineChange?.count || 0} rows within this branch.
  Rename only this row or all matching rows in this branch? All child entries will
  be kept.
</Modal>

<style>
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .framework-assistant-chat.review-step
    :global(.paxbot-fullscreen-chat .chat-stage) {
    justify-content: flex-start;
    padding-top: 1.75rem;
  }
  /* Overrides the negative show-greeting-when-input margin regardless of stylesheet order, so the heading spacing is the chat-stage --control-gap. */
  .framework-assistant-chat.review-step
    :global(.paxbot-fullscreen-chat.hasInput .chat-stage .suggestions-slot) {
    margin-top: 0;
  }
  .framework-assistant-chat.review-step .framework-result-helper {
    max-width: 80ch;
    margin-inline: auto;
    margin-bottom: 1.25rem;
    color: var(--pax-white-new);
  }
  .pilot-readiness {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .readiness-summary {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .readiness-summary strong {
    font-size: 0.875rem;
    font-weight: 600;
  }
  .readiness-summary p {
    font-size: 0.8125rem;
    color: var(--pax-text-secondary-new);
  }
  .readiness-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--pax-text-secondary-new);
    flex-shrink: 0;
  }
  .ready .readiness-dot {
    background: var(--pax-state-success-new, #65a87a);
  }
  .framework-area-actions.review-footer {
    width: 100%;
    max-width: 76rem;
    margin-inline: auto;
    align-items: center;
    justify-content: flex-end;
    padding: 0.75rem 0;
    flex-wrap: wrap;
  }
  .draft-status {
    margin-right: auto;
    color: var(--pax-text-secondary-new);
    font-size: 0.75rem;
  }
  .draft-status button {
    margin-left: 0.5rem;
  }
  .framework-area-suggestions .work-alternative button {
    background: transparent;
    border: 0;
    padding: 0.25rem;
    text-decoration: underline;
  }
  .activity-breadcrumb {
    color: var(--pax-grey);
    font-size: 0.75rem;
    overflow-wrap: anywhere;
  }
  .activity-split-help {
    font-size: 0.8125rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  @media (max-width: 60rem) {
    .review-controls {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
    }
    .pilot-readiness {
      flex-wrap: wrap;
    }
  }

  .pilot-readiness {
    padding: 1rem;
    border: 1px solid var(--pax-grey);
    border-radius: 0.5rem;
    margin: 0.75rem 0;
  }
  .pilot-readiness p {
    margin: 0.5rem 0;
  }
  .pilot-selection {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    white-space: nowrap;
  }
  .pilot-selection input {
    width: auto;
    margin: 0;
  }
  .task-mining-fit {
    flex-wrap: wrap;
  }

  .framework-assistant-chat {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    background: var(--pax-bg-canvas-new);
  }

  .framework-assistant-chat :global(.paxbot-fullscreen-chat) {
    flex: 1;
    min-width: 0;
    min-height: 0;
  }

  .framework-assistant-chat :global(.paxbot-fullscreen-chat .chat-stage),
  .framework-assistant-chat :global(.paxbot-fullscreen-chat .start-screen),
  .framework-assistant-chat :global(.paxbot-fullscreen-chat .start-actions),
  .framework-area-suggestions,
  .framework-area-actions,
  .work-description-panels,
  .start-paths,
  .start-path-detail {
    gap: var(--control-gap);
  }

  .framework-assistant-chat
    :global(.paxbot-fullscreen-chat .chat-stage .suggestions-slot),
  .framework-assistant-chat :global(.paxbot-fullscreen-chat .start-subtitle) {
    margin: 0;
  }

  .framework-area-suggestions {
    display: flex;
    min-width: 0;
    width: 100%;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    max-width: 80ch;
  }

  .framework-assistant-chat
    :global(.paxbot-fullscreen-chat.hasInput .chat-stage) {
    width: min(100%, 80ch);
  }

  .framework-assistant-chat.review-step
    :global(.paxbot-fullscreen-chat .chat-stage) {
    width: 100%;
    min-width: 0;
    max-width: none;
    padding-inline: 2rem;
  }

  .framework-assistant-chat.review-step .framework-area-suggestions {
    max-width: none;
  }

  .framework-assistant-chat.review-step :global(.suggestions-slot) {
    width: 100%;
    max-width: 76rem;
    min-width: 0;
  }

  .framework-assistant-chat.review-step .review-meta {
    width: min(100%, 76rem);
    margin-inline: auto;
  }

  .framework-assistant-chat.review-step
    :global(.paxbot-fullscreen-chat .composer-row) {
    width: min(calc(var(--paxbot-view-width) - 4rem), 80ch);
    margin-inline: auto;
  }

  .framework-area-helper {
    flex-basis: 100%;
    margin: 0;
    color: var(--pax-white);
    font-size: 0.9rem;
    text-align: center;
  }

  .framework-area-suggestions button {
    padding: 0.5rem 0.8rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 999px;
    background: var(--pax-surface-control-new);
    color: var(--pax-white-new);
    font: inherit;
    font-size: 0.9rem;
    cursor: pointer;
  }

  .framework-area-suggestions button:hover {
    background: var(--pax-surface-control-hover-new);
    border-color: var(--pax-white-new);
    color: var(--pax-white-new);
  }

  .framework-area-suggestions button.selected,
  .framework-area-suggestions button.selected:hover {
    background: var(--pax-white-new);
    border-color: var(--pax-white-new);
    color: var(--pax-black-new);
  }

  .department-step :global(.suggestions-slot) {
    width: 100%;
    margin: 0;
  }

  .department-step .framework-area-helper {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }

  .work-description-step :global(.suggestions-slot) {
    width: 100%;
    min-width: 0;
    margin-top: 0;
  }

  .work-description-step :global(.greeting) {
    width: 100%;
    min-width: 0;
  }

  .work-description-step :global(.greeting h1) {
    width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .work-description-step .framework-area-helper {
    max-width: 44rem;
    margin-bottom: 0;
    line-height: 1.55;
  }

  .department-choice-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--control-gap);
    width: min(36rem, 100%);
    margin: 0 auto;
  }

  .choice-list {
    display: grid;
    flex: 1 1 18rem;
    min-width: 0;
    gap: var(--control-gap);
  }

  .work-description-panels {
    display: grid;
    width: min(100%, 40rem);
  }

  /* Gefuehrter Einstieg in Schritt 7: vier Wege als Karten */
  .start-paths {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    /* ragt minimal ueber den Container hinaus, damit die Karten etwas breiter sind */
    width: calc(100% + 1rem);
    margin: 0 -0.5rem;
  }

  .start-paths button {
    display: grid;
    gap: var(--control-gap);
    align-content: start;
    padding: 0.75rem;
    border: 1px solid var(--pax-border-new);
    border-radius: var(--br);
    background: transparent;
    color: var(--pax-text-primary-new);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .start-paths button.active {
    border-color: var(--pax-white-new);
    background: var(--pax-surface-control-new);
  }

  .start-paths strong {
    color: var(--pax-white-new);
    font-size: 0.85rem;
    font-weight: 400;
    letter-spacing: 0.04em;
    line-height: 1.45;
  }

  .start-paths span {
    color: var(--pax-text-secondary-new);
    font-size: 0.85rem;
    line-height: 1.45;
  }

  .start-path-detail {
    display: grid;
    justify-items: center;
    width: 100%;
    margin-bottom: 0;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .start-path-detail p {
    max-width: 44rem;
    margin: 0;
    text-align: center;
  }

  .survey-projects {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--control-gap, 0.5rem);
    margin-top: 0.75rem;
  }

  /* Zweiter Gedanke als eigener Block (Rueckmeldung 04.10.) */
  .start-path-detail .start-path-note {
    margin-top: 0;
    color: var(--pax-text-secondary-new);
  }

  .task-hint {
    display: flex;
    justify-content: center;
    color: var(--pax-text-secondary-new);
    font-size: 0.85rem;
  }

  .task-hint :global(.tooltip-text) {
    width: min(18rem, calc(100vw - 2rem));
    max-width: min(18rem, calc(100vw - 2rem));
    box-sizing: border-box;
    overflow-wrap: anywhere;
    white-space: normal;
    text-align: left;
  }

  .task-hint-tooltip-content > strong {
    display: block;
    margin-bottom: 0.4rem;
  }

  .task-hint-tooltip-content p {
    margin: 0 0 0.5rem;
  }

  .task-hint-tooltip-content p:last-child {
    margin-bottom: 0;
  }

  .team-step :global(.paxbot-fullscreen-chat .composer-row) {
    position: static;
    width: 100%;
    padding-top: 0;
    transform: none;
    background: none;
    flex-shrink: 0;
  }

  .team-step :global(.paxbot-fullscreen-chat .suggestions-slot) {
    width: 100%;
    min-width: 0;
    flex-shrink: 0;
  }

  .team-step :global(.paxbot-fullscreen-chat .chat-stage) {
    min-height: 0;
    overflow-y: auto;
    justify-content: safe center;
  }

  .team-step .framework-area-helper:only-child {
    margin-bottom: calc(1rem - var(--control-gap));
  }

  .team-continue {
    display: flex;
    justify-content: center;
    margin-top: calc(1rem - var(--control-gap));
  }

  .team-step .framework-area-suggestions {
    row-gap: 1rem;
  }

  .team-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--control-gap);
    width: 100%;
    min-width: 0;
  }

  .team-tag {
    display: inline-flex;
    align-items: center;
    gap: var(--control-gap);
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .team-tag img {
    width: 0.625rem;
    height: 0.625rem;
    flex-shrink: 0;
  }

  @media (max-width: 900px) {
    .start-paths {
      grid-template-columns: 1fr 1fr;
    }
  }

  .work-guidance {
    width: 100%;
    margin: 0;
    padding: 1.25rem;
    box-sizing: border-box;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.5rem;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .work-guidance strong {
    display: block;
  }

  .work-guidance-prompts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    margin: 1rem 0;
    padding: 0;
    list-style: none;
  }

  .work-guidance-prompts li {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.7rem 0.75rem;
    border: 1px solid var(--pax-divider-new);
    border-radius: 0.375rem;
  }

  .work-guidance-prompts span {
    color: var(--pax-text-secondary-new);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .work-guidance-prompts strong {
    color: var(--pax-white);
    font-weight: 500;
    line-height: 1.35;
  }

  .work-guidance-example {
    margin: 0.75rem 0 0;
    color: var(--pax-text-secondary-new);
  }

  .work-guidance-example span {
    color: var(--pax-white);
    font-weight: 600;
  }

  @media (max-width: 36rem) {
    .work-guidance-prompts {
      grid-template-columns: 1fr;
    }
  }

  .review-toolbar {
    width: 100%;
    margin-bottom: 1rem;
  }

  .review-controls {
    width: 100%;
    display: grid;
    grid-template-columns: minmax(12rem, 1fr) minmax(10rem, 14rem) auto;
    align-items: center;
    gap: 0.5rem;
  }

  .review-controls label,
  .row-editor label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.875rem;
  }

  .review-controls label {
    width: 100%;
  }

  .review-controls input,
  .review-controls select,
  .row-editor input,
  .row-editor select {
    min-width: 0;
    max-width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.35rem;
    background: var(--pax-surface-control-new);
    color: var(--pax-white-new);
    font: inherit;
  }
  .review-controls input,
  .review-controls select {
    box-sizing: border-box;
    width: 100%;
    height: 2.5rem;
  }

  .framework-table .process-group th {
    position: static;
    text-align: left;
    padding: 0.5rem;
    background: var(--pax-surface-control-new);
  }

  .framework-table .process-group button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    text-align: left;
    border: 0;
    border-radius: 0.25rem;
  }

  .process-chevron {
    width: 0.7rem;
    height: 0.45rem;
    transition: transform 0.15s ease;
  }

  .process-chevron.collapsed {
    transform: rotate(-90deg);
  }

  .process-group span {
    color: var(--pax-text-secondary-new);
    font-weight: normal;
  }

  .row-editor {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
  }

  .row-editor.hierarchy-editor {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .row-editor.hierarchy-editor > p,
  .row-editor.hierarchy-editor > .framework-area-error {
    grid-column: 1 / -1;
  }

  .row-editor label {
    gap: 0.5rem;
    color: var(--pax-black-new);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .row-editor input,
  .row-editor select {
    box-sizing: border-box;
    width: 100%;
    min-height: 2.75rem;
    padding: 0.6875rem 0.75rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.375rem;
    outline: none;
    background: var(--pax-white-new);
    color: var(--pax-black-new);
    font: inherit;
    transition:
      border-color 120ms ease,
      box-shadow 120ms ease;
  }

  .row-editor input::placeholder {
    color: var(--pax-grey);
    opacity: 1;
  }

  .row-editor input:hover,
  .row-editor select:hover {
    border-color: var(--pax-border-strong-new);
  }

  .row-editor input:focus-visible,
  .row-editor select:focus-visible {
    border-color: var(--pax-orange);
    box-shadow: 0 0 0 1px var(--pax-orange);
  }

  .row-editor input:read-only {
    background: var(--pax-smoke);
    color: var(--pax-grey);
  }

  :global(.custom-modal.framework-row-editor-modal .bx--modal-container) {
    width: min(40rem, calc(100vw - 2rem)) !important;
    max-width: calc(100vw - 2rem) !important;
    padding: 0;
  }

  :global(.custom-modal.framework-row-editor-modal .bx--modal-header) {
    margin: 0;
    padding: 1.25rem 3.5rem 1rem 1.25rem;
  }

  :global(.custom-modal.framework-row-editor-modal .bx--modal-content) {
    padding: 0 1.25rem 1.25rem !important;
  }

  :global(.custom-modal.framework-row-editor-modal .modal_wrapper) {
    padding-bottom: 0 !important;
  }

  :global(
      .custom-modal.framework-row-editor-modal
        .modal_wrapper
        > .divider:first-child
    ) {
    display: none;
  }

  :global(.custom-modal.framework-row-editor-modal .buttons-container) {
    margin-top: 1rem;
  }

  :global(.custom-modal.framework-row-editor-modal .actions-buttons-container) {
    flex-wrap: wrap;
  }

  .row-editor p {
    margin: 0;
  }

  .row-editor p,
  .analysis-preview p {
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .analysis-diff {
    max-height: 24rem;
    overflow: auto;
    overflow-wrap: anywhere;
  }

  .analysis-diff h4 {
    font-size: 1rem;
    margin: 1rem 0 0.5rem;
  }

  .analysis-diff ul {
    padding-left: 1.25rem;
  }

  .analysis-diff li {
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  .framework-area-actions {
    display: flex;
    flex-basis: 100%;
    justify-content: center;
    gap: 0.5rem;
  }

  .framework-result-actions {
    margin-top: 0;
  }

  .framework-saved-actions {
    display: flex;
    width: min(100%, 48rem);
    max-width: 100%;
    flex-basis: auto;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    box-sizing: border-box;
  }

  .framework-saved-actions :global(button) {
    width: auto;
    min-width: 0;
  }

  .framework-success-helper {
    margin-bottom: 0.5rem;
  }

  .knowledge-options {
    display: flex;
    flex-basis: 100%;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
  }

  .framework-area-suggestions .knowledge-options button {
    min-width: 10rem;
    border-color: var(--pax-white-new);
    background: transparent;
  }

  .framework-area-suggestions .knowledge-options button.selected,
  .framework-area-suggestions .knowledge-options button.selected:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: var(--pax-white-new);
    color: var(--pax-white-new);
  }

  .framework-table-wrap {
    width: 100%;
    min-width: 0;
    max-width: min(100%, 76rem);
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: visible;
    scrollbar-width: thin;
    scrollbar-color: var(--pax-text-secondary-new) var(--pax-bg-canvas-new);
    border: 1px solid var(--pax-border-new);
    border-radius: 0.5rem;
    background: var(--pax-bg-canvas-new);
  }

  .framework-table {
    width: 100%;
    min-width: 60rem;
    table-layout: fixed;
    border-collapse: collapse;
    background: transparent;
    color: var(--pax-white-new);
    font-size: 1rem;
    text-align: left;
  }

  .framework-col-l1 {
    width: 17%;
  }
  .framework-col-l2 {
    width: 18%;
  }
  .framework-col-l3 {
    width: 18%;
  }
  .framework-col-l4 {
    width: 21%;
  }
  .framework-col-fit {
    width: 16%;
  }
  .framework-col-tm {
    width: 10%;
  }
  .framework-col-delete {
    width: 10%;
  }

  .framework-table td {
    background-color: var(--pax-bg-canvas-new) !important;
  }

  .framework-table tbody tr:nth-child(even) td {
    background-color: var(--pax-surface-raised-color-new) !important;
  }

  .framework-table tbody tr:hover td {
    background-color: var(--pax-surface-control-hover-new) !important;
  }

  .framework-result-helper {
    color: var(--pax-white);
    font-size: 0.8rem;
    text-align: center;
  }

  .framework-table th,
  .framework-table td {
    box-sizing: border-box;
    padding: 0.75rem 0.7rem;
    border-bottom: 1px solid var(--pax-text-secondary-new);
    vertical-align: middle;
  }

  .framework-table tbody td {
    height: 3.25rem;
  }

  .framework-table th:last-child,
  .framework-table td:last-child {
    text-align: center;
  }

  .framework-row-actions {
    display: grid;
    grid-template-columns: repeat(2, 2rem);
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }

  .tm-fit-cell {
    text-align: center;
  }

  .tm-fit-header {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .tm-fit-label {
    white-space: nowrap;
  }

  .tm-fit-header :global(.tooltip-text) {
    width: min(16rem, calc(100vw - 2rem));
    max-width: min(16rem, calc(100vw - 2rem));
    box-sizing: border-box;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .tm-fit-tooltip-content > strong {
    display: block;
    margin-bottom: 0.4rem;
  }

  .tm-fit-tooltip-content p {
    margin: 0 0 0.5rem;
  }

  .tm-fit-tooltip-content ul {
    display: grid;
    gap: 0.4rem;
    margin: 0.7rem 0 0;
    padding: 0;
    list-style: none;
  }

  .tm-fit-tooltip-content li {
    display: grid;
    grid-template-columns: 1.15rem minmax(0, 1fr);
    align-items: flex-start;
    gap: 0.45rem;
  }

  .tm-fit-tooltip-content li > span:last-child {
    min-width: 0;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .tm-tooltip-status {
    display: inline-flex;
    width: 1.15rem;
    height: 1.15rem;
    flex: 0 0 1.15rem;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: var(--pax-black-new);
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
  }

  .tm-tooltip-status.suitable {
    background: var(--pax-state-success-new);
  }
  .tm-tooltip-status.uncertain {
    background: var(--pax-state-warning-new);
  }
  .tm-tooltip-status.unsuitable {
    background: var(--pax-state-danger-new);
  }

  .framework-area-suggestions .tm-fit-button,
  .framework-area-suggestions .tm-fit-button:hover {
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid var(--pax-state-warning-new);
    border-radius: 50%;
    background: var(--pax-state-warning-new);
    color: var(--pax-black-new);
    cursor: pointer;
    font: inherit;
    font-weight: 600;
  }

  .framework-area-suggestions .tm-fit-button.suitable,
  .framework-area-suggestions .tm-fit-button.suitable:hover {
    border-color: var(--pax-state-success-new, #65a87a);
    background: var(--pax-state-success-new, #65a87a);
    color: var(--pax-black-new);
  }

  .framework-area-suggestions .tm-fit-button.notSuitable,
  .framework-area-suggestions .tm-fit-button.notSuitable:hover {
    border-color: var(--pax-state-danger-new);
    background: var(--pax-state-danger-new);
    color: var(--pax-black-new);
  }

  .tm-fit-button:focus-visible {
    outline: 2px solid var(--pax-orange);
    outline-offset: 0.2rem;
  }

  .framework-cell {
    display: flex;
    min-width: 0;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0.35rem;
  }

  .framework-cell :global(.paxbot-inline-edit) {
    width: auto;
    min-width: 0;
    flex: 1 1 auto;
  }

  .task-mining-fit {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.5rem;
  }

  .task-mining-fit :global(.badge) {
    box-sizing: border-box;
    height: 1.2rem;
    padding: 0.15rem 0.45rem;
    flex: 0 0 auto;
  }

  .task-mining-fit :global(.badge_color span.small) {
    font-size: 0.72rem;
    line-height: 0.8rem;
  }

  .fit-badge-button {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }

  .framework-area-suggestions .fit-badge-button,
  .framework-area-suggestions .fit-badge-button:hover {
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
  }

  .fit-badge-button:focus-visible {
    border-radius: 999px;
    outline: 2px solid var(--pax-white-new);
    outline-offset: 2px;
  }

  .task-mining-fit :global(.tooltip) {
    flex: 0 0 auto;
    color: var(--pax-text-secondary-new);
  }

  .task-mining-fit :global(.tooltip-icon) {
    width: 1.05rem;
    height: 1.05rem;
  }

  .fit-review {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    color: var(--pax-black-new);
    min-width: 0;
    line-height: 1.5;
  }

  :global(.custom-modal.fit-review-modal .bx--modal-container) {
    width: min(42rem, calc(100vw - 2rem)) !important;
    max-width: calc(100vw - 2rem) !important;
  }

  :global(.custom-modal.fit-review-modal .bx--modal-header) {
    padding: 1.25rem 3.5rem 1rem 1.25rem;
    margin: 0;
  }

  :global(.custom-modal.fit-review-modal .bx--modal-content) {
    padding: 0 1.25rem 1.25rem !important;
  }

  :global(
      .custom-modal.fit-review-modal .bx--modal-content--overflow-indicator
    ) {
    display: none;
  }

  :global(.custom-modal.fit-review-modal .modal_wrapper) {
    padding-bottom: 0 !important;
    min-width: 0;
  }

  :global(
      .custom-modal.fit-review-modal .modal_wrapper > .divider:first-child
    ) {
    display: none;
  }

  :global(.custom-modal.fit-review-modal .actions-buttons-container) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .framework-table-wrap:focus-visible {
    outline: 2px solid var(--pax-orange);
    outline-offset: 0.2rem;
  }

  @media (max-width: 40rem) {
    .framework-assistant-chat.review-step
      :global(.paxbot-fullscreen-chat .chat-stage) {
      padding-inline: 1rem;
    }

    .framework-assistant-chat.review-step
      :global(.paxbot-fullscreen-chat .composer-row) {
      width: calc(var(--paxbot-view-width) - 2rem);
    }

    .review-footer .draft-status {
      flex-basis: 100%;
      margin-bottom: 0.25rem;
    }
  }

  .fit-review p {
    margin: 0;
  }


  .fit-review-questions {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
  }

  .fit-review fieldset {
    display: flex;
    margin: 0;
    padding: 0.9rem 1rem 1rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.4rem;
    flex-direction: column;
    gap: 0.7rem;
  }

  .fit-review legend {
    padding: 0;
    color: var(--pax-black-new);
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.4;
  }

  .fit-review-options {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
  }

  .fit-review-options button {
    padding: 0.5rem 0.7rem;
    border: 1px solid #b8b8b8;
    border-radius: 999px;
    background: white;
    color: var(--pax-black-new);
    font: inherit;
    font-size: 0.78rem;
    cursor: pointer;
  }

  .fit-review-options button:hover,
  .fit-review-options button:focus-visible {
    border-color: var(--pax-black-new);
    outline: none;
  }

  .fit-review-options button.unknown-option {
    margin-left: auto;
  }

  .fit-review-options button.selected {
    border-color: var(--pax-black-new);
    background: var(--pax-black-new);
    color: white;
  }

  .framework-cell :global(.badge) {
    box-sizing: border-box;
    height: 1rem;
    padding: 0.1rem 0.3rem;
    flex: 0 0 auto;
    transform: scale(0.78);
    transform-origin: center;
  }

  .framework-cell :global(.badge_color span.small) {
    font-size: 0.7rem;
    line-height: 0.7rem;
  }

  .framework-table th {
    position: sticky;
    top: 0;
    background: var(--pax-surface-control-new);
    font-size: 1rem;
    font-weight: 400;
  }

  .framework-table thead th {
    z-index: 1;
  }

  .framework-table thead th:first-child {
    border-top-left-radius: 0.5rem;
  }

  .framework-table thead th:last-child {
    border-top-right-radius: 0.5rem;
  }

  .framework-table tbody tr:last-child td {
    border-bottom: 0;
  }

  .framework-table tbody tr:last-child td:first-child {
    border-bottom-left-radius: 0.5rem;
  }

  .framework-table tbody tr:last-child td:last-child {
    border-bottom-right-radius: 0.5rem;
  }

  .framework-area-error {
    flex-basis: 100%;
    margin: 0;
    color: var(--pax-state-danger-new);
    font-size: 0.8rem;
    text-align: center;
  }
  /* Bei einem frueheren Merge verlorene Regeln aus really_unstable */
  .framework-area-actions.work-entry-back {
    flex: 0 0 auto;
  }

  .framework-assistant-chat.work-description-step
    :global(.paxbot-fullscreen-chat .chat-stage) {
    min-height: 0;
    overflow-y: auto;
    justify-content: safe center;
  }

  .framework-assistant-chat.work-description-step
    :global(.paxbot-fullscreen-chat .chat-stage),
  .work-description-step .framework-area-suggestions {
    gap: 1rem;
  }

  :global(.custom-modal.framework-new-department-modal) {
    z-index: 100000;
  }

  :global(.custom-modal.framework-new-department-modal .bx--modal-container) {
    width: min(28rem, calc(100vw - 2rem)) !important;
    max-width: calc(100vw - 2rem) !important;
  }

  :global(.custom-modal.framework-new-department-modal .bx--modal-content) {
    padding: 0 1rem;
  }

  :global(
      .custom-modal.framework-new-department-modal
        .bx--modal-scroll-content
        > .modal_wrapper
    ) {
    padding-bottom: var(--control-gap) !important;
  }

  :global(.custom-modal.framework-new-department-modal .divider) {
    margin-bottom: var(--control-gap);
  }

  :global(.custom-modal.framework-new-department-modal .divider-margin) {
    margin-top: var(--control-gap);
    margin-bottom: 0;
  }

  :global(.custom-modal.framework-new-department-modal .buttons-container) {
    margin-top: var(--control-gap);
    gap: var(--control-gap);
  }

  :global(
      .custom-modal.framework-new-department-modal .actions-buttons-container
    ) {
    width: 100%;
    justify-content: space-between;
    gap: var(--control-gap);
  }

  .new-department-error {
    margin: var(--control-gap) 0 0;
    color: var(--pax-danger);
  }

  .choice-list :global(.selector .more-button) {
    height: 2.5rem;
    padding: 0 0.875rem;
    border-radius: var(--br);
  }
</style>
